import { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Home, Plus, User, Menu, ArrowLeft, X, MapPin, Phone, Mail, Building2, Briefcase, ChevronDown, Check, Calendar, ClipboardList, CheckCircle2, Edit, CreditCard, FileText, Camera, MapPinned, Upload, Image, Trash2, Star, Download, History, DollarSign, TrendingUp, Target, Clock, Bell, MessageCircle, CheckCircle, XCircle, AlertCircle, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dynamic Page Title Hook
const usePageTitle = (title) => {
  useEffect(() => {
    document.title = title ? `${title} | Milanam` : "Milanam - Find. Hire. Done.";
  }, [title]);
};

// File Upload Component (Demo UI)
const FileUploadField = ({ label, value, onChange, accept = "image/*,.pdf", placeholder = "Or paste URL here" }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [preview, setPreview] = useState(value || null);
  const [fileName, setFileName] = useState("");

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Demo: Show preview without actual upload
      if (file.size > 200 * 1024) {
        alert("File size should be less than 200 KB");
        return;
      }
      setFileName(file.name);
      // Create local preview for demo
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
        onChange(e.target.result); // In real app, this would be the uploaded URL
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        alert("File size should be less than 200 KB");
        return;
      }
      setFileName("Camera capture");
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
        onChange(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    if (url) {
      setPreview(url);
      setFileName("");
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setFileName("");
    onChange("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="relative inline-block">
          <div className="w-24 h-24 rounded-lg border-2 border-blue-200 overflow-hidden bg-gray-100">
            {preview.startsWith("data:application/pdf") || preview.endsWith(".pdf") ? (
              <div className="w-full h-full flex items-center justify-center bg-red-50">
                <FileText className="w-8 h-8 text-red-500" />
              </div>
            ) : (
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            )}
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          {fileName && <p className="text-xs text-gray-500 mt-1 truncate max-w-[96px]">{fileName}</p>}
        </div>
      )}

      {/* Upload Options */}
      {!preview && (
        <div className="border-2 border-dashed border-blue-200 rounded-xl p-4 bg-blue-50/50">
          <div className="flex flex-wrap gap-2 justify-center mb-3">
            {/* File Upload Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors text-sm"
            >
              <Upload className="w-4 h-4" />
              Choose File
            </button>
            
            {/* Camera Capture Button */}
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-green-300 rounded-lg text-green-700 hover:bg-green-50 transition-colors text-sm"
            >
              <Camera className="w-4 h-4" />
              Capture
            </button>
          </div>
          
          <p className="text-xs text-gray-400 text-center mb-3">File size should be less than 200 KB</p>
          
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="flex-1 border-t border-gray-200"></div>
            <span>OR</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>
          
          {/* URL Input */}
          <input
            type="text"
            value={value && !value.startsWith("data:") ? value : ""}
            onChange={handleUrlChange}
            placeholder={placeholder}
            className="w-full mt-3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all placeholder:text-gray-400"
          />
          
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCameraCapture}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

// Location Capture Component (Demo UI for Business Partners)
const LocationCaptureField = ({ label, value, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locationData, setLocationData] = useState(value ? JSON.parse(value) : null);

  const handleCaptureLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setLoading(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        setLocationData(coords);
        onChange(JSON.stringify(coords));
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError("Location access denied. Please enable location permissions.");
            break;
          case err.POSITION_UNAVAILABLE:
            setError("Location information unavailable.");
            break;
          case err.TIMEOUT:
            setError("Location request timed out.");
            break;
          default:
            setError("An error occurred while getting location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleRemove = () => {
    setLocationData(null);
    onChange("");
    setError("");
  };

  const openInMaps = () => {
    if (locationData) {
      window.open(`https://www.google.com/maps?q=${locationData.latitude},${locationData.longitude}`, '_blank');
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Location Captured */}
      {locationData && (
        <div className="border-2 border-green-200 rounded-xl p-4 bg-green-50/50">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MapPinned className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">Location Captured</p>
                <p className="text-xs text-green-600 mt-1">
                  Lat: {locationData.latitude.toFixed(6)}
                </p>
                <p className="text-xs text-green-600">
                  Long: {locationData.longitude.toFixed(6)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Accuracy: ±{Math.round(locationData.accuracy)}m
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1 text-red-500 hover:bg-red-50 rounded-full transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={openInMaps}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              <MapPin className="w-4 h-4" />
              View on Maps
            </button>
            <button
              type="button"
              onClick={handleCaptureLocation}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-green-300 text-green-700 rounded-lg text-sm hover:bg-green-50 transition-colors disabled:opacity-50"
            >
              <MapPinned className="w-4 h-4" />
              Update
            </button>
          </div>
        </div>
      )}

      {/* Capture Location UI */}
      {!locationData && (
        <div className="border-2 border-dashed border-blue-200 rounded-xl p-4 bg-blue-50/50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <MapPinned className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Capture your business location for customers to find you easily
            </p>
            <button
              type="button"
              onClick={handleCaptureLocation}
              disabled={loading}
              className="flex items-center justify-center gap-2 mx-auto px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Capturing...
                </>
              ) : (
                <>
                  <MapPinned className="w-5 h-5" />
                  Capture My Location
                </>
              )}
            </button>
            <p className="text-xs text-gray-400 mt-3">
              This will use your device's GPS to get your exact location
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};


// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("milanam_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [userType, setUserType] = useState(() => {
    return localStorage.getItem("milanam_user_type") || "customer";
  });

  const login = (userData, type = "customer") => {
    setUser(userData);
    setUserType(type);
    localStorage.setItem("milanam_user", JSON.stringify(userData));
    localStorage.setItem("milanam_user_type", type);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("milanam_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setUserType("customer");
    localStorage.removeItem("milanam_user");
    localStorage.removeItem("milanam_user_type");
  };

  return (
    <AuthContext.Provider value={{ user, userType, login, logout, updateUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Milanam Logo Component - Using uploaded logo image
const MilanamLogo = () => (
  <img 
    src="/milanam-logo.png" 
    alt="MILANAM"
    className="h-6"
    style={{ 
      filter: "brightness(0) saturate(100%) invert(13%) sepia(62%) saturate(2878%) hue-rotate(220deg) brightness(92%) contrast(95%)"
    }}
  />
);

// Top Navigation Bar
const TopNavBar = ({ showBack = false, title = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, userType, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const isHomePage = location.pathname === "/";

  const menuItems = [
    { label: "Partner with us", path: "/partner" },
    { label: "Register as Associate", path: "/register-associate" },
    { label: "Contact us", path: "/contact" },
    { label: "B2B Services", path: "/b2b-services" },
    { label: "Admin Dashboard", path: "/admin" },
  ];

  // User type labels
  const userTypeLabels = {
    business: { label: "Business", color: "bg-purple-500" },
    associate: { label: "Associate", color: "bg-green-500" },
    customer: { label: "User", color: "bg-blue-500" },
    user: { label: "User", color: "bg-blue-500" }
  };
  const typeInfo = userTypeLabels[userType] || userTypeLabels.customer;

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50 h-16" data-testid="top-navbar">
      <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
        {/* Left: Back button + Logo */}
        <div className="flex items-center gap-3">
          {!isHomePage && (
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 hover:bg-blue-50 rounded-full transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-blue-700" />
            </button>
          )}
          <button onClick={() => navigate("/")} data-testid="logo-button">
            <MilanamLogo />
          </button>
        </div>

        {/* Center: Title (if any) */}
        {title && (
          <h1 className="text-lg font-semibold text-blue-900 hidden md:block">{title}</h1>
        )}

        {/* Right: Menu + Login */}
        <div className="flex items-center gap-3">
          {/* Menu Dropdown */}
          <div className="relative">
            <button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-blue-50 rounded-full transition-colors"
              data-testid="menu-button"
            >
              <Menu className="w-5 h-5 text-blue-700" />
            </button>
            
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-12 bg-white rounded-lg shadow-lg border border-blue-100 py-2 w-52 z-50" data-testid="menu-dropdown">
                  {menuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMenuOpen(false); }}
                      className="w-full px-4 py-3 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      data-testid={`menu-item-${item.path.slice(1)}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Auth Button */}
          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2">
                <span className="text-sm text-blue-900 font-medium">
                  {userType === "business" ? (user.business_name || user.owner_name) : user.name}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs text-white ${typeInfo.color}`}>
                  {typeInfo.label}
                </span>
              </div>
              <button 
                onClick={logout}
                className="px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          ) : (
            <button 
              onClick={() => navigate("/login")}
              className="px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors"
              data-testid="login-button"
            >
              Login / Signup
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

// Bottom Navigation Bar
const BottomNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [showCreatePost, setShowCreatePost] = useState(false);

  const navItems = [
    { icon: Home, label: "Home", path: "/" },
    { icon: Plus, label: "Post", path: "/create-post", isCenter: true },
    { icon: User, label: "Profile", path: "/profile" },
  ];

  const handleNavClick = (item) => {
    if (item.path === "/create-post") {
      if (!isAuthenticated) {
        navigate("/login");
      } else {
        setShowCreatePost(true);
      }
    } else if (item.path === "/profile" && !isAuthenticated) {
      navigate("/login");
    } else {
      navigate(item.path);
    }
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 h-16" data-testid="bottom-navbar">
        <div className="max-w-lg mx-auto h-full flex items-center justify-around px-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            if (item.isCenter) {
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item)}
                  className="w-14 h-14 -mt-6 bg-blue-700 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-800 transition-colors"
                  data-testid="add-post-button"
                >
                  <Icon className="w-7 h-7 text-white" />
                </button>
              );
            }
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item)}
                className={`flex flex-col items-center gap-1 py-2 px-4 rounded-lg transition-colors ${
                  isActive 
                    ? "text-blue-700 bg-blue-50" 
                    : "text-gray-500 hover:text-blue-700 hover:bg-blue-50"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Create Post Modal */}
      {showCreatePost && <CreatePostModal onClose={() => setShowCreatePost(false)} />}
    </>
  );
};

// City Select Dropdown
const CitySelect = ({ value, onChange, className = "" }) => {
  const [cities, setCities] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await axios.get(`${API}/cities`);
        setCities(res.data.cities);
      } catch (e) {
        console.error("Error fetching cities:", e);
        setCities(["Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune"]);
      }
    };
    fetchCities();
  }, []);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl text-left flex items-center justify-between hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
        data-testid="city-select"
      >
        <span className={value ? "text-gray-900" : "text-gray-500"}>
          {value || "Choose your city"}
        </span>
        <ChevronDown className={`w-5 h-5 text-blue-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto" data-testid="city-dropdown">
            {cities.map((city) => (
              <button
                key={city}
                onClick={() => { onChange(city); setIsOpen(false); }}
                className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-blue-50 transition-colors ${
                  value === city ? "bg-blue-50 text-blue-700" : "text-gray-700"
                }`}
                data-testid={`city-option-${city.toLowerCase()}`}
              >
                <span>{city}</span>
                {value === city && <Check className="w-4 h-4 text-blue-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Create Post Modal
const CreatePostModal = ({ onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    photos: [],
    location: "",
    city: "",
    google_location: "",
    captured_location: "",
    contact_name: user?.name || "",
    contact_phone: user?.phone || "",
    service_type: "",
    preferred_date: "",
    preferred_time: "",
    urgency: "normal",
  });
  const [loading, setLoading] = useState(false);
  const [capturingLocation, setCapturingLocation] = useState(false);
  
  // Voice AI states
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [voiceStatus, setVoiceStatus] = useState("idle"); // idle, recording, processing, done, error
  const [sessionId] = useState(() => `voice-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const serviceTypes = [
    "Plumbing", "Electrical", "Carpentry", "Painting", "AC Repair", 
    "Deep Cleaning", "Pest Control", "Appliance Repair", "Masonry",
    "Roofing", "Flooring", "Interior Design", "Other"
  ];

  // Start voice recording
  const startRecording = async () => {
    try {
      // Check if mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setVoiceStatus("no-mic");
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await processVoice(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setVoiceStatus("recording");
      setTranscription("");
    } catch (err) {
      console.error("Microphone access error:", err);
      if (err.name === 'NotFoundError' || err.name === 'NotAllowedError') {
        setVoiceStatus("no-mic");
      } else {
        setVoiceStatus("error");
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setVoiceStatus("processing");
    }
  };

  // Process voice with AI
  const processVoice = async (audioBlob) => {
    setIsProcessing(true);
    setVoiceStatus("processing");
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, 'recording.webm');
      formDataUpload.append('session_id', sessionId);
      
      const res = await axios.post(`${API}/voice-enquiry`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTranscription(res.data.transcription);
      
      // Auto-fill form with extracted data
      if (res.data.extracted_data) {
        const extracted = res.data.extracted_data;
        setFormData(prev => ({
          ...prev,
          service_type: extracted.service_type || prev.service_type,
          title: extracted.title || prev.title,
          description: extracted.description || prev.description,
          location: extracted.location || prev.location,
          city: extracted.city || prev.city,
          preferred_date: extracted.preferred_date || prev.preferred_date,
          preferred_time: extracted.preferred_time || prev.preferred_time,
          urgency: extracted.urgency || prev.urgency,
          contact_name: extracted.contact_name || prev.contact_name,
          contact_phone: extracted.contact_phone || prev.contact_phone,
        }));
        setVoiceStatus("done");
      } else {
        setVoiceStatus("done");
      }
    } catch (err) {
      console.error("Voice processing error:", err);
      setVoiceStatus("error");
      alert("Failed to process voice. Please try again or fill manually.");
    }
    setIsProcessing(false);
  };

  const handleCaptureLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    setCapturingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        setFormData({ ...formData, captured_location: JSON.stringify(coords) });
        setCapturingLocation(false);
      },
      (err) => {
        setCapturingLocation(false);
        alert("Could not get your location. Please try again or enter manually.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.city || !formData.service_type) {
      alert("Please fill in all required fields (Title, Description, City, Service Type)");
      return;
    }
    if (!formData.preferred_date || !formData.preferred_time) {
      alert("Please select your preferred date and time for the service");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/posts?user_id=${user.id}`, formData);
      alert("Service request posted successfully! Associates will send you quotations soon.");
      onClose();
    } catch (error) {
      console.error("Error creating post:", error);
      alert("Failed to create post");
    }
    setLoading(false);
  };

  const handlePhotoAdd = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      setFormData({ ...formData, photos: [...formData.photos, url] });
    }
  };

  const locationData = formData.captured_location ? JSON.parse(formData.captured_location) : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-post-modal">
      <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-xl font-bold">Create Service Request</h2>
          <button onClick={onClose} className="p-1 hover:bg-blue-600 rounded-full transition-colors" data-testid="close-create-post">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Voice AI Agent */}
        <div className="px-6 pt-4">
          <div className="border-2 border-purple-200 rounded-xl overflow-hidden bg-gradient-to-br from-purple-50 to-indigo-50">
            <div className="px-4 py-3 border-b border-purple-200 bg-white/50">
              <p className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                AI Voice Assistant
              </p>
              <p className="text-xs text-purple-600 mt-0.5">Speak in any language - I'll fill the form for you!</p>
            </div>
            
            <div className="p-4 flex flex-col items-center">
              {/* Show text input if no mic available */}
              {voiceStatus === "no-mic" ? (
                <div className="w-full space-y-3">
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-700">Microphone not available. Type your requirement instead:</p>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={transcription}
                      onChange={(e) => setTranscription(e.target.value)}
                      placeholder="E.g., I need a plumber tomorrow morning for tap leak in Koramangala"
                      className="flex-1 px-3 py-2 text-sm border border-purple-200 rounded-lg focus:outline-none focus:border-purple-400"
                      data-testid="text-input-fallback"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!transcription.trim()) return;
                        setIsProcessing(true);
                        setVoiceStatus("processing");
                        try {
                          // Use the chat endpoint as fallback for text
                          const today = new Date().toISOString().split('T')[0];
                          const res = await axios.post(`${API}/ai-enquiry-text`, {
                            text: transcription,
                            session_id: sessionId
                          });
                          if (res.data.extracted_data) {
                            const extracted = res.data.extracted_data;
                            setFormData(prev => ({
                              ...prev,
                              service_type: extracted.service_type || prev.service_type,
                              title: extracted.title || prev.title,
                              description: extracted.description || prev.description,
                              location: extracted.location || prev.location,
                              city: extracted.city || prev.city,
                              preferred_date: extracted.preferred_date || prev.preferred_date,
                              preferred_time: extracted.preferred_time || prev.preferred_time,
                              urgency: extracted.urgency || prev.urgency,
                              contact_name: extracted.contact_name || prev.contact_name,
                              contact_phone: extracted.contact_phone || prev.contact_phone,
                            }));
                          }
                          setVoiceStatus("done");
                        } catch (err) {
                          console.error("Text processing error:", err);
                          setVoiceStatus("no-mic");
                        }
                        setIsProcessing(false);
                      }}
                      disabled={isProcessing || !transcription.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    >
                      {isProcessing ? "..." : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Mic Button */}
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg ${
                      isRecording 
                        ? "bg-red-500 hover:bg-red-600 animate-pulse" 
                        : isProcessing
                        ? "bg-purple-400 cursor-not-allowed"
                        : "bg-gradient-to-br from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                    }`}
                    data-testid="voice-record-button"
                  >
                    {isProcessing ? (
                      <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : isRecording ? (
                      <div className="w-6 h-6 bg-white rounded-sm"></div>
                    ) : (
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1 1.93c-3.94-.49-7-3.85-7-7.93h2c0 3.31 2.69 6 6 6s6-2.69 6-6h2c0 4.08-3.06 7.44-7 7.93V19h4v2H8v-2h4v-3.07z"/>
                      </svg>
                    )}
                  </button>
                  
                  {/* Status Text */}
                  <p className={`mt-3 text-sm font-medium ${
                    voiceStatus === "recording" ? "text-red-600" :
                    voiceStatus === "processing" ? "text-purple-600" :
                    voiceStatus === "done" ? "text-green-600" :
                    voiceStatus === "error" ? "text-red-500" :
                    "text-gray-500"
                  }`}>
                    {voiceStatus === "idle" && "Tap mic & speak your requirement"}
                    {voiceStatus === "recording" && "🎤 Listening... Tap to stop"}
                    {voiceStatus === "processing" && "Processing your voice..."}
                    {voiceStatus === "done" && "✓ Form filled! Review below"}
                    {voiceStatus === "error" && "Error - Please try again"}
                  </p>
                </>
              )}
              
              {/* Transcription Display */}
              {transcription && voiceStatus === "done" && (
                <div className="mt-3 w-full p-3 bg-white rounded-lg border border-purple-200">
                  <p className="text-xs text-gray-500 mb-1">You said:</p>
                  <p className="text-sm text-gray-800 italic">"{transcription}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Type *</label>
            <select
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all bg-white"
              data-testid="service-type-select"
            >
              <option value="">Select service type</option>
              {serviceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <CitySelect 
            value={formData.city} 
            onChange={(city) => setFormData({ ...formData, city })} 
          />
          
          <input
            type="text"
            placeholder="Title / Brief Summary *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
            data-testid="post-title-input"
          />
          
          <textarea
            placeholder="Describe the work needed in detail *"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none placeholder:text-gray-500"
            data-testid="post-description-input"
          />

          {/* Urgency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
            <div className="flex gap-2">
              {[
                { id: "urgent", label: "Urgent", color: "red" },
                { id: "normal", label: "Normal", color: "blue" },
                { id: "flexible", label: "Flexible", color: "green" }
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, urgency: opt.id })}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    formData.urgency === opt.id
                      ? opt.color === "red" ? "bg-red-500 text-white" 
                        : opt.color === "blue" ? "bg-blue-500 text-white"
                        : "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
              <input
                type="date"
                value={formData.preferred_date}
                onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all"
                data-testid="preferred-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
              <select
                value={formData.preferred_time}
                onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all bg-white"
                data-testid="preferred-time-select"
              >
                <option value="">Select time</option>
                <option value="morning">Morning (8 AM - 12 PM)</option>
                <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                <option value="evening">Evening (4 PM - 8 PM)</option>
                <option value="anytime">Anytime</option>
              </select>
            </div>
          </div>

          {/* Location Section */}
          <div className="space-y-3 p-4 bg-blue-50 rounded-xl">
            <label className="block text-sm font-medium text-gray-700">Service Location *</label>
            
            <input
              type="text"
              placeholder="Address / Area / Landmark"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500 bg-white"
              data-testid="post-location-input"
            />

            {/* GPS Location Capture */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCaptureLocation}
                disabled={capturingLocation}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {capturingLocation ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Capturing...
                  </>
                ) : (
                  <>
                    <MapPinned className="w-4 h-4" />
                    Capture GPS Location
                  </>
                )}
              </button>
            </div>

            {locationData && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Location captured: {locationData.latitude.toFixed(4)}, {locationData.longitude.toFixed(4)}
                </p>
              </div>
            )}
            
            <input
              type="text"
              placeholder="Or paste Google Maps link"
              value={formData.google_location}
              onChange={(e) => setFormData({ ...formData, google_location: e.target.value })}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500 bg-white"
              data-testid="post-google-location-input"
            />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Contact Name *"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
              data-testid="post-contact-name-input"
            />
            <input
              type="tel"
              placeholder="Contact Phone"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
              data-testid="post-contact-phone-input"
            />
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photos (helps get accurate quotes)</label>
            <button
              type="button"
              onClick={handlePhotoAdd}
              className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              data-testid="add-photo-button"
            >
              <Camera className="w-5 h-5" />
              Add Photos
            </button>
            {formData.photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.photos.map((photo, idx) => (
                  <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 relative">
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, photos: formData.photos.filter((_, i) => i !== idx) })}
                      className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-bl-lg"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
            data-testid="submit-post-button"
          >
            {loading ? "Posting..." : "Post Service Request"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  usePageTitle("Login");
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("select-type"); // select-type, phone, otp
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [phoneOrEmail, setPhoneOrEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const userTypes = [
    {
      id: "customer",
      title: "Hire an Associate",
      subtitle: "(User)",
      description: "Looking to hire skilled professionals for your work",
      icon: User,
      color: "blue"
    },
    {
      id: "associate",
      title: "I'm an Associate",
      subtitle: "(Service Provider)",
      description: "Offer your services and connect with customers",
      icon: Briefcase,
      color: "green"
    },
    {
      id: "business",
      title: "Business Partner",
      subtitle: "(Business)",
      description: "Register your business and expand your reach",
      icon: Building2,
      color: "purple"
    }
  ];

  const handleSelectType = (typeId) => {
    setSelectedUserType(typeId);
    setStep("phone");
  };

  const [existingUserData, setExistingUserData] = useState(null);
  const [newUserName, setNewUserName] = useState("");
  const [newUserCity, setNewUserCity] = useState("");

  const [demoOtp, setDemoOtp] = useState("");
  
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!phoneOrEmail) {
      setError("Please enter mobile number or email");
      return;
    }
    
    setLoading(true);
    setError("");
    try {
      // Check if user exists first
      const userCheck = await axios.get(`${API}/auth/user-by-phone/${phoneOrEmail}`);
      if (userCheck.data.found) {
        setExistingUserData(userCheck.data);
      } else {
        setExistingUserData(null);
      }
      
      await axios.post(`${API}/auth/send-otp`, { phone_or_email: phoneOrEmail });
      setStep("otp");
    } catch (err) {
      setError("Failed to send OTP");
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError("Please enter 6 digit OTP");
      return;
    }
    
    // For new users, require name
    if (!existingUserData && !newUserName.trim()) {
      setError("Please enter your full name");
      return;
    }
    
    // For new users, require city
    if (!existingUserData && !newUserCity) {
      setError("Please select your city");
      return;
    }
    
    setLoading(true);
    setError("");
    try {
      if (existingUserData) {
        // Existing user - verify OTP and login
        const res = await axios.post(`${API}/auth/verify-otp`, { 
          phone_or_email: phoneOrEmail, 
          otp 
        });
        
        if (res.data.success) {
          login(existingUserData.data, existingUserData.type);
          navigate("/profile");
        }
      } else {
        // New user - verify OTP then register based on type
        const res = await axios.post(`${API}/auth/verify-otp`, { 
          phone_or_email: phoneOrEmail, 
          otp 
        });
        
        if (res.data.success || res.data.is_new_user) {
          // Register based on selected type
          const isEmail = phoneOrEmail.includes("@");
          
          if (selectedUserType === "associate") {
            // Register as associate
            const associateData = {
              name: newUserName.trim(),
              phone: isEmail ? "" : phoneOrEmail,
              email: isEmail ? phoneOrEmail : "",
              city: newUserCity,
              services: [],
              experience: "",
              description: ""
            };
            const regRes = await axios.post(`${API}/associates`, associateData);
            login(regRes.data, "associate");
            navigate("/profile");
          } else if (selectedUserType === "business") {
            // Register as business
            const businessData = {
              business_name: newUserName.trim(),
              owner_name: newUserName.trim(),
              phone: isEmail ? "" : phoneOrEmail,
              email: isEmail ? phoneOrEmail : "",
              city: newUserCity,
              category: "Other",
              description: ""
            };
            const regRes = await axios.post(`${API}/businesses`, businessData);
            login(regRes.data, "business");
            navigate("/profile");
          } else {
            // Register as customer
            const isEmail = phoneOrEmail.includes("@");
            const userData = {
              name: newUserName.trim(),
              phone: isEmail ? "" : phoneOrEmail,
              email: isEmail ? phoneOrEmail : "",
              city: newUserCity,
              user_type: "customer"
            };
            const regRes = await axios.post(`${API}/auth/register`, userData);
            login(regRes.data, "customer");
            navigate("/profile");
          }
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.response?.data?.detail || "Invalid OTP. Please try again.");
    }
    setLoading(false);
  }; 

  const getTypeInfo = () => {
    return userTypes.find(t => t.id === selectedUserType);
  };

  // Get existing user's display name
  const getExistingUserName = () => {
    if (!existingUserData) return null;
    const data = existingUserData.data;
    if (existingUserData.type === "business") {
      return data.business_name || data.owner_name;
    }
    return data.name;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-blue-900">
            {step === "select-type" && "LOGIN / SIGN UP"}
            {step === "phone" && "WELCOME"}
            {step === "otp" && "VERIFY OTP"}
            {step === "register" && "Complete Your Profile"}
          </h1>
          <p className="text-gray-600 mt-2">
            {step === "select-type" && "Choose how you want to use Milanam"}
            {step === "phone" && getTypeInfo()?.description}
            {step === "otp" && "Enter the OTP sent to your device"}
            {step === "register" && "Tell us about yourself"}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Step 1: Select User Type */}
        {step === "select-type" && (
          <div className="space-y-4">
            {userTypes.map((type) => {
              const Icon = type.icon;
              const colorClasses = {
                blue: "border-blue-200 hover:border-blue-500 hover:bg-blue-50",
                green: "border-green-200 hover:border-green-500 hover:bg-green-50",
                purple: "border-purple-200 hover:border-purple-500 hover:bg-purple-50"
              };
              const iconColors = {
                blue: "bg-blue-100 text-blue-600",
                green: "bg-green-100 text-green-600",
                purple: "bg-purple-100 text-purple-600"
              };
              const badgeColors = {
                blue: "bg-blue-500",
                green: "bg-green-500",
                purple: "bg-purple-500"
              };
              
              return (
                <button
                  key={type.id}
                  onClick={() => handleSelectType(type.id)}
                  className={`w-full p-4 border-2 rounded-xl text-left transition-all ${colorClasses[type.color]}`}
                  data-testid={`login-type-${type.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${iconColors[type.color]}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{type.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs text-white ${badgeColors[type.color]}`}>
                          {type.subtitle}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Step 2: Enter Phone */}
        {step === "phone" && (
          <form onSubmit={handleSendOTP} className="space-y-4">
            {/* Show selected type badge */}
            {selectedUserType && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${
                  selectedUserType === "customer" ? "bg-blue-500" :
                  selectedUserType === "associate" ? "bg-green-500" : "bg-purple-500"
                }`}>
                  {getTypeInfo()?.subtitle}
                </span>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number or Email
              </label>
              <input
                type="text"
                value={phoneOrEmail}
                onChange={(e) => setPhoneOrEmail(e.target.value)}
                placeholder="Enter mobile number or email"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="phone-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
              data-testid="send-otp-button"
            >
              {loading ? "Sending..." : "SEND OTP"}
            </button>
            <button
              type="button"
              onClick={() => { setStep("select-type"); setSelectedUserType(null); }}
              className="w-full py-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              ← Change login type
            </button>
          </form>
        )}

        {/* Step 3: Verify OTP */}
        {step === "otp" && (
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            {/* Show selected type badge */}
            {selectedUserType && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${
                  selectedUserType === "customer" ? "bg-blue-500" :
                  selectedUserType === "associate" ? "bg-green-500" : "bg-purple-500"
                }`}>
                  {getTypeInfo()?.subtitle}
                </span>
              </div>
            )}

            {/* Show existing user's name or ask for new user's name */}
            {existingUserData ? (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center mb-4">
                <p className="text-sm text-green-600 mb-1">Welcome back!</p>
                <p className="text-xl font-bold text-green-800">{getExistingUserName()}</p>
                <p className="text-xs text-green-600 mt-1">{phoneOrEmail}</p>
              </div>
            ) : (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4 space-y-3">
                <p className="text-sm text-blue-600 mb-2 text-center">New to Milanam? Complete your details</p>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder={selectedUserType === "business" ? "Enter business name" : "Enter your full name"}
                  className="w-full px-4 py-3 border-2 border-blue-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500 bg-white"
                  data-testid="new-user-name-input"
                />
                <select
                  value={newUserCity}
                  onChange={(e) => setNewUserCity(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-blue-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                  data-testid="new-user-city-select"
                >
                  <option value="">Select your city</option>
                  <option value="Mumbai">Mumbai</option>
                  <option value="Delhi">Delhi</option>
                  <option value="Bangalore">Bangalore</option>
                  <option value="Hyderabad">Hyderabad</option>
                  <option value="Chennai">Chennai</option>
                  <option value="Kolkata">Kolkata</option>
                  <option value="Pune">Pune</option>
                  <option value="Ahmedabad">Ahmedabad</option>
                  <option value="Jaipur">Jaipur</option>
                  <option value="Lucknow">Lucknow</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter 6 Digit OTP
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6 digit OTP"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-center text-2xl tracking-widest placeholder:text-gray-500 placeholder:text-base placeholder:tracking-normal"
                maxLength={6}
                data-testid="otp-input"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">Check your email for the OTP</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
              data-testid="verify-otp-button"
            >
              {loading ? "Verifying..." : existingUserData ? "VERIFY & LOGIN" : "VERIFY & REGISTER"}
            </button>
            <button
              type="button"
              onClick={() => setStep("phone")}
              className="w-full py-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              Change number/email
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Available Services Data
const SERVICES = [
  { name: "Painting", icon: "🎨" },
  { name: "Plumber", icon: "🔧" },
  { name: "Mechanic", icon: "🔩" },
  { name: "Electrician", icon: "⚡" },
  { name: "Construction", icon: "🏗️" },
  { name: "Salon", icon: "💇" },
  { name: "Deep Cleaning", icon: "🧹" },
];

// Testimonials Data
const TESTIMONIALS = [
  {
    name: "Rajesh Kumar",
    role: "Shop Owner",
    image: "https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=100&h=100&fit=crop",
    quote: "Milanam helped me connect with skilled electricians in my area. Highly recommended!"
  },
  {
    name: "Priya Sharma",
    role: "Associate",
    image: "https://images.unsplash.com/photo-1568873722373-0c4d3f27eb91?w=100&h=100&fit=crop",
    quote: "Found great opportunities through Milanam. The platform is simple and effective."
  }
];

// Home Page
const HomePage = () => {
  usePageTitle(null); // Shows default "Milanam - Find. Hire. Done."
  const [selectedCity, setSelectedCity] = useState("");
  const [stats, setStats] = useState({ businesses: 127, associates: 284, customers: 1520 });
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, postsRes] = await Promise.all([
          axios.get(`${API}/stats${selectedCity ? `?city=${selectedCity}` : ""}`),
          axios.get(`${API}/posts${selectedCity ? `?city=${selectedCity}` : ""}`)
        ]);
        // Use fetched stats
        setStats({
          businesses: statsRes.data.businesses || 127,
          associates: statsRes.data.associates || 284,
          customers: statsRes.data.customers || 1520
        });
        setPosts(postsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, [selectedCity]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8" data-testid="home-page">
      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <button 
            onClick={() => navigate("/partner")}
            className="px-8 py-3 bg-blue-700 text-white font-bold rounded-full hover:bg-blue-800 transition-colors shadow-lg"
            data-testid="partner-button"
          >
            PARTNER WITH US
          </button>
          <button 
            onClick={() => navigate("/hire-associate")}
            className="px-8 py-3 bg-white text-blue-700 font-bold rounded-full border-2 border-blue-700 hover:bg-blue-50 transition-colors shadow-lg"
            data-testid="hire-associate-button"
          >
            HIRE ASSOCIATE
          </button>
        </div>

        {/* Tagline */}
        <h1 className="text-5xl md:text-7xl font-black text-blue-900 mb-4 tracking-tight">
          FIND. HIRE. DONE.
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Need Help? Find <span className="font-bold text-blue-700">VERIFIED & TRUSTED</span> local associate instantly.
        </p>

        {/* City Select */}
        <div className="max-w-md mx-auto mb-8">
          <p className="text-sm font-semibold text-gray-700 mb-2">SELECT YOUR CITY</p>
          <CitySelect value={selectedCity} onChange={setSelectedCity} />
        </div>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-6 min-w-[160px] md:min-w-[180px]">
            <p className="text-3xl md:text-4xl font-bold text-blue-700">{stats.businesses}</p>
            <p className="text-xs md:text-sm text-gray-600 font-medium">BUSINESSES REGISTERED</p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 min-w-[160px] md:min-w-[180px]">
            <p className="text-3xl md:text-4xl font-bold text-green-600">{stats.associates}</p>
            <p className="text-xs md:text-sm text-gray-600 font-medium">ASSOCIATES REGISTERED</p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 min-w-[160px] md:min-w-[180px]">
            <p className="text-3xl md:text-4xl font-bold text-orange-500">{stats.customers}</p>
            <p className="text-xs md:text-sm text-gray-600 font-medium">HAPPY CUSTOMERS</p>
          </div>
        </div>
      </div>

      {/* Who We Are Section */}
      <div className="bg-white py-16" data-testid="who-we-are-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 text-center mb-4">Who We Are</h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-8">
            Platform Connecting Locals to grow their Businesses, increase their reach to clients locally.
          </p>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            MILANAM provides a hustle free platform to get your any kind of work done as per your convenience and urgency with verified partners.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Benefits */}
            <div>
              <h3 className="text-xl font-bold text-blue-800 mb-6">Benefits</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Reliable, trustworthy</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Immediate solution</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-700">No Platform fee for hiring associates</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Instant invoice generation for bill processing</span>
                </li>
              </ul>
            </div>
            
            {/* Images */}
            <div className="flex gap-4 justify-center">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1645597454479-14cb0e8bcabe?w=400&h=400&fit=crop" 
                  alt="Worker" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden shadow-lg mt-8">
                <img 
                  src="https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=400&h=400&fit=crop" 
                  alt="Shopkeeper" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Available Services Section */}
      <div className="py-16 bg-gradient-to-br from-blue-50 to-white" data-testid="services-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 text-center mb-12">Available Services</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {SERVICES.map((service) => (
              <div 
                key={service.name}
                className="bg-white rounded-2xl shadow-md p-4 text-center hover:shadow-lg hover:scale-105 transition-all cursor-pointer"
              >
                <div className="text-4xl mb-2">{service.icon}</div>
                <p className="text-sm font-medium text-gray-700">{service.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* From Local Hands Section */}
      <div className="py-16 bg-blue-700 text-white" data-testid="local-hands-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">From Local Hands to Your Home</h2>
          <p className="text-blue-100 text-lg leading-relaxed">
            Every festive light, handcrafted decor, or wooden masterpiece carries the warmth of someone's hard work. 
            Our platform helps artisans, designers, and makers bring their creations to life and find customers who value real craftsmanship.
          </p>
          <p className="text-blue-200 mt-4 font-medium">
            Let's celebrate local talent — not just during festivals, but all year round.
          </p>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 text-center mb-12">What People Say</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {TESTIMONIALS.map((testimonial, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-blue-200">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-900">{testimonial.name}</h3>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Scheduled Service Card with Chat and Contact Details
const ScheduledServiceCard = ({ post, acceptedQuote, isServiceProvider, currentUser, userType, formatCurrency }) => {
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Load messages when chat is opened
  useEffect(() => {
    if (showChat) {
      loadMessages();
    }
  }, [showChat]);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/posts/${post.id}/messages`);
      setMessages(res.data);
    } catch (e) {
      console.error("Error loading messages:", e);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setLoading(true);
    try {
      const senderName = userType === "business" ? currentUser.business_name : currentUser.name;
      await axios.post(`${API}/posts/${post.id}/messages`, {
        sender_id: currentUser.id,
        sender_name: senderName,
        sender_type: userType,
        message: newMessage.trim()
      });
      setNewMessage("");
      loadMessages();
    } catch (e) {
      console.error("Error sending message:", e);
      alert("Failed to send message");
    }
    setLoading(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return "Today";
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // Contact info based on user type
  const contactInfo = isServiceProvider ? {
    name: post.contact_name,
    phone: post.contact_phone,
    location: post.location || post.city,
    label: "User Details"
  } : {
    name: acceptedQuote?.associate_name,
    phone: acceptedQuote?.associate_phone,
    location: post.city,
    label: "Service Provider Details",
    type: acceptedQuote?.associate_type === "business" ? "Business Partner" : "Service Associate"
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
      {/* Compact Header with Case ID */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-white/90" />
          <div>
            <span className="text-white/80 text-[10px] uppercase tracking-wide">Case </span>
            <span className="text-white font-semibold text-sm">#{post.unique_id || post.id?.slice(0, 8).toUpperCase()}</span>
          </div>
        </div>
        <span className="px-2 py-0.5 bg-white/20 text-white rounded-full text-[10px] font-medium">
          {post.service_type || "General"}
        </span>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {/* Title & Description */}
        <h3 className="font-semibold text-base text-gray-900 leading-tight">{post.title}</h3>
        <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{post.description}</p>

        {/* Compact Schedule & Location Row */}
        <div className="flex items-center gap-2 mt-3 text-xs">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 rounded-lg">
            <Calendar className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-gray-700 font-medium">{post.preferred_date || "TBD"}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-50 rounded-lg">
            <Clock className="w-3.5 h-3.5 text-purple-600" />
            <span className="text-gray-700 font-medium">
              {post.preferred_time === "morning" ? "8-12 AM" : 
               post.preferred_time === "afternoon" ? "12-4 PM" :
               post.preferred_time === "evening" ? "4-8 PM" : "Flexible"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-orange-50 rounded-lg flex-1 min-w-0">
            <MapPin className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />
            <span className="text-gray-700 font-medium truncate">{post.location || post.city}</span>
          </div>
        </div>

        {/* Contact Person - Compact */}
        <div className="mt-3 p-2.5 bg-gray-50 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {contactInfo.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-sm text-gray-900 leading-tight">{contactInfo.name}</p>
              <p className="text-[10px] text-gray-500">{isServiceProvider ? "User" : contactInfo.type || "Provider"}</p>
            </div>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
              showDetails ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700 hover:bg-blue-200"
            }`}
          >
            {showDetails ? "Hide" : "Details"}
          </button>
        </div>

        {/* Agreed Amount - Compact */}
        {acceptedQuote && (
          <div className="mt-3 px-3 py-2 bg-green-50 rounded-lg border border-green-100 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Agreed Amount</p>
              <p className="text-lg font-bold text-green-600 leading-tight">
                {formatCurrency(acceptedQuote.min_estimate)} - {formatCurrency(acceptedQuote.max_estimate)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500">Site Visit</p>
              <p className="text-sm font-semibold text-gray-700">{formatCurrency(acceptedQuote.site_visit_charge)}</p>
            </div>
          </div>
        )}

        {/* Action Buttons - Compact */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setShowChat(!showChat)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
              showChat ? "bg-purple-600 text-white" : "bg-purple-50 text-purple-700 hover:bg-purple-100"
            }`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {showChat ? "Close" : "Chat"}
          </button>
          <button
            onClick={async () => {
              if (window.confirm("Are you sure you want to cancel this scheduled visit?")) {
                try {
                  await axios.put(`${API}/posts/${post.id}/cancel?cancelled_by=${isServiceProvider ? 'service_provider' : 'user'}`);
                  alert("Visit cancelled. " + (isServiceProvider ? "User will be notified." : "You can view other quotations."));
                  window.location.reload();
                } catch (e) {
                  alert("Failed to cancel visit");
                }
              }
            }}
            className="flex-1 py-2 px-3 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 transition-all"
          >
            <XCircle className="w-3.5 h-3.5" />
            Cancel
          </button>
        </div>

        {/* Complete Visit Button - Compact */}
        {isServiceProvider && (
          <button
            onClick={() => setShowCompleteModal(true)}
            className="w-full mt-2 py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 transition-all shadow-sm"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Complete Visit & Generate Invoice
          </button>
        )}
      </div>

      {/* Contact Details Panel */}
      {showDetails && (
        <div className="px-4 pb-4">
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <User className="w-5 h-5" />
              {contactInfo.label}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{contactInfo.name}</p>
                  {contactInfo.type && (
                    <p className="text-xs text-gray-500">{contactInfo.type}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <Phone className="w-4 h-4 text-green-600" />
                <a href={`tel:${contactInfo.phone}`} className="text-green-700 font-medium hover:underline">
                  {contactInfo.phone}
                </a>
                <button
                  onClick={() => navigator.clipboard.writeText(contactInfo.phone)}
                  className="ml-auto text-xs text-gray-500 hover:text-gray-700"
                >
                  Copy
                </button>
              </div>
              
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <MapPin className="w-4 h-4 text-red-500" />
                <span className="text-gray-700">{contactInfo.location}</span>
              </div>

              {post.captured_location && (
                <button
                  onClick={() => {
                    const loc = JSON.parse(post.captured_location);
                    window.open(`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`, '_blank');
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MapPinned className="w-4 h-4" />
                  Open in Google Maps
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat Panel */}
      {showChat && (
        <div className="border-t border-gray-100">
          <div className="p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Chat
            </h4>
            
            {/* Messages */}
            <div className="h-64 overflow-y-auto bg-white rounded-lg p-3 space-y-3 mb-3">
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isMe = msg.sender_id === currentUser.id;
                  const showDate = idx === 0 || formatMessageDate(msg.timestamp) !== formatMessageDate(messages[idx - 1]?.timestamp);
                  return (
                    <div key={msg.id || idx}>
                      {showDate && (
                        <div className="text-center text-xs text-gray-400 my-2">
                          {formatMessageDate(msg.timestamp)}
                        </div>
                      )}
                      <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                          isMe ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'
                        }`}>
                          {!isMe && (
                            <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>
                          )}
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-xs mt-1 ${isMe ? 'text-blue-200' : 'text-gray-500'}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Message Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !newMessage.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Visit Modal */}
      {showCompleteModal && (
        <CompleteVisitModal
          post={post}
          acceptedQuote={acceptedQuote}
          formatCurrency={formatCurrency}
          onClose={() => setShowCompleteModal(false)}
          onComplete={() => {
            setShowCompleteModal(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

// Complete Visit Modal - Generate Invoice with actual costs
const CompleteVisitModal = ({ post, acceptedQuote, formatCurrency, onClose, onComplete }) => {
  const [items, setItems] = useState([
    { description: "Site Visit Charge", amount: acceptedQuote?.site_visit_charge || 50 },
  ]);
  const [newItem, setNewItem] = useState({ description: "", amount: 0 });
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const addItem = () => {
    if (newItem.description && newItem.amount > 0) {
      setItems([...items, { ...newItem }]);
      setNewItem({ description: "", amount: 0 });
    }
  };

  const removeItem = (index) => {
    if (index === 0) return; // Don't remove site visit charge
    setItems(items.filter((_, i) => i !== index));
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  const handleComplete = async () => {
    if (items.length === 0) {
      alert("Please add at least one item to the invoice");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(`${API}/posts/${post.id}/complete-with-invoice`, {
        items,
        total_amount: totalAmount,
        notes
      });
      alert("Visit completed! Invoice generated successfully.");
      onComplete();
    } catch (e) {
      console.error("Error completing visit:", e);
      alert("Failed to complete visit");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-green-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-xl font-bold">Complete Visit & Generate Invoice</h2>
            <p className="text-xs text-green-200">Post ID: {post.id?.slice(0, 8).toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-green-500 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Service Summary */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <h3 className="font-bold text-gray-900">{post.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{post.service_type} • {post.contact_name}</p>
          </div>

          {/* Invoice Items */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3">Invoice Items</h4>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="flex-1 text-sm">{item.description}</span>
                  <span className="font-medium text-green-700">{formatCurrency(item.amount)}</span>
                  {idx > 0 && (
                    <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add Item */}
            <div className="flex gap-2 mt-3">
              <input
                type="text"
                placeholder="Description (e.g., Labor, Parts)"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <input
                type="number"
                placeholder="₹"
                value={newItem.amount || ""}
                onChange={(e) => setNewItem({ ...newItem, amount: parseInt(e.target.value) || 0 })}
                className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <button
                onClick={addItem}
                className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Total */}
          <div className="p-4 bg-green-50 rounded-xl flex justify-between items-center">
            <span className="font-semibold text-gray-700">Total Amount</span>
            <span className="text-2xl font-bold text-green-700">{formatCurrency(totalAmount)}</span>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 resize-none"
              placeholder="Any additional notes..."
            />
          </div>

          <button
            onClick={handleComplete}
            disabled={loading}
            className="w-full py-4 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Complete Visit & Generate Invoice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


// Profile Page
const ProfilePage = () => {
  usePageTitle("My Profile");
  const { user, userType, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState("sub-home");
  const [posts, setPosts] = useState([]);
  const [availablePosts, setAvailablePosts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showEditPost, setShowEditPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  const isServiceProvider = userType === "associate" || userType === "business";

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    
    const fetchPosts = async () => {
      try {
        if (isServiceProvider) {
          // For associates/business: fetch based on tab
          if (activeTab === "sub-home") {
            // Fetch all available posts to quote on (not limited to city)
            const res = await axios.get(`${API}/posts/available/all`);
            setAvailablePosts(res.data);
            setPosts([]);
          } else if (activeTab === "followups") {
            // Fetch posts where they've submitted quotes (waiting for customer response)
            const res = await axios.get(`${API}/posts/quoted/${user.id}`);
            setPosts(res.data);
            setAvailablePosts([]);
          } else if (activeTab === "scheduled") {
            // Fetch scheduled posts where their quote was accepted
            const res = await axios.get(`${API}/posts/scheduled/${user.id}`);
            setPosts(res.data);
            setAvailablePosts([]);
          }
        } else {
          // For customers: fetch their own posts
          if (user.id) {
            if (activeTab === "sub-home") {
              // Show ALL posts (active, quoted) in My Requests
              const res = await axios.get(`${API}/posts/user/${user.id}`);
              setPosts(res.data.filter(p => p.status !== "scheduled" && p.status !== "completed"));
            } else if (activeTab === "scheduled") {
              const res = await axios.get(`${API}/posts/user/${user.id}?status=scheduled`);
              setPosts(res.data);
            } else if (activeTab === "followups") {
              // Show posts with quotations to review (any post that has quotations)
              const res = await axios.get(`${API}/posts/user/${user.id}`);
              setPosts(res.data.filter(p => p.quotations && p.quotations.length > 0 && p.status !== "scheduled"));
            }
          }
        }
      } catch (e) {
        console.error("Error fetching posts:", e);
      }
    };
    
    // Fetch profile stats
    const fetchStats = async () => {
      try {
        if (user.id || user.phone) {
          const identifier = user.id || user.phone;
          const res = await axios.get(`${API}/profile-stats/${userType}/${identifier}`);
          setProfileStats(res.data);
        }
      } catch (e) {
        console.error("Error fetching profile stats:", e);
        // Set empty stats for new users - no dummy data
        setProfileStats({
          rating: 0,
          total_reviews: 0,
          visits_completed: 0,
          total_earning: 0,
          highest_earning: 0,
          average_earning: 0,
          max_range_serviced: 0,
          service_requests: 0,
          joining_date: user.created_at || new Date().toISOString()
        });
      }
    };
    
    // Fetch updates/notifications
    const fetchUpdates = async () => {
      try {
        if (user.id || user.phone) {
          const identifier = user.id || user.phone;
          const res = await axios.get(`${API}/updates/${userType}/${identifier}`);
          setUpdates(res.data);
        }
      } catch (e) {
        console.error("Error fetching updates:", e);
        // Set empty updates for new users - no dummy data
        setUpdates([]);
      }
    };
    
    fetchPosts();
    fetchStats();
    fetchUpdates();
  }, [user, activeTab, navigate, userType, isServiceProvider, refreshKey]);

  if (!user) return null;

  // Different tab labels based on user type
  const tabs = isServiceProvider ? [
    { id: "sub-home", label: "Available Jobs", icon: Briefcase },
    { id: "followups", label: "Quoted", icon: ClipboardList },
    { id: "scheduled", label: "Scheduled", icon: Calendar },
  ] : [
    { id: "sub-home", label: "My Requests", icon: Home },
    { id: "followups", label: "Quotations", icon: ClipboardList },
    { id: "scheduled", label: "Scheduled", icon: Calendar },
  ];

  // Handle quote submission
  const handleOpenQuoteModal = (post) => {
    setSelectedPost(post);
    setShowQuoteModal(true);
  };

  // Handle accepting a quotation (for customers)
  const handleAcceptQuotation = async (postId, quotationId) => {
    try {
      await axios.put(`${API}/posts/${postId}/quote/${quotationId}/accept`);
      alert("Quotation accepted! Service has been scheduled.");
      // Refresh posts
      setActiveTab("scheduled");
    } catch (error) {
      console.error("Error accepting quotation:", error);
      alert("Failed to accept quotation");
    }
  };

  // Get display name based on user type
  const displayName = userType === "business" ? user.business_name || user.owner_name : user.name;
  const displaySubtitle = userType === "business" ? user.owner_name : null;
  
  // User type labels with descriptions
  const userTypeLabels = {
    business: { label: "Business", subtitle: "(Business Partner)", color: "bg-purple-500" },
    associate: { label: "Associate", subtitle: "(Service Provider)", color: "bg-green-500" },
    customer: { label: "User", subtitle: "(User)", color: "bg-blue-500" },
    user: { label: "User", subtitle: "(User)", color: "bg-blue-500" }
  };
  const typeInfo = userTypeLabels[userType] || userTypeLabels.customer;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
      } else if (i === fullStars && hasHalf) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400/50 text-yellow-400" />);
      } else {
        stars.push(<Star key={i} className="w-4 h-4 text-gray-300" />);
      }
    }
    return stars;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 pt-20 pb-8" data-testid="profile-page">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur overflow-hidden border-4 border-white/50">
              <img 
                src={user.photo_url || user.shop_photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${displayName}`} 
                alt={displayName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold">{displayName}</h1>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                  {typeInfo.label} {typeInfo.subtitle}
                </span>
              </div>
              {displaySubtitle && (
                <p className="text-blue-200 text-sm">Owner: {displaySubtitle}</p>
              )}
              <p className="text-blue-100 flex items-center gap-2 mt-1">
                <MapPin className="w-4 h-4" />
                {user.city}
              </p>
              {user.phone && (
                <p className="text-blue-100 flex items-center gap-2 mt-1">
                  <Phone className="w-4 h-4" />
                  {user.phone}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowEditModal(true)}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              data-testid="edit-profile-button"
            >
              <Edit className="w-5 h-5" />
            </button>
          </div>
          
          {/* Additional info based on user type */}
          {userType === "associate" && user.services && user.services.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {user.services.map((service, idx) => (
                <span key={idx} className="px-3 py-1 bg-white/20 rounded-full text-sm">
                  {service}
                </span>
              ))}
            </div>
          )}
          
          {userType === "business" && user.category && (
            <div className="mt-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                {user.category}
              </span>
              {user.year_of_establishment && (
                <span className="ml-2 text-blue-200 text-sm">Est. {user.year_of_establishment}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Left Sidebar - Stats */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-md p-5 sticky top-24">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Stats
              </h3>
              
              {/* Rating Section */}
              {profileStats && (
                <div className="space-y-4">
                  {/* Rating */}
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      {userType === "customer" ? "Rating by Associates" : "Rating by Clients"}
                    </p>
                    {profileStats.total_reviews > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="flex">{renderStars(profileStats.rating || 0)}</div>
                        <span className="font-bold text-yellow-700">{profileStats.rating?.toFixed(1)}</span>
                        <span className="text-xs text-gray-400">({profileStats.total_reviews} reviews)</span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No ratings yet</p>
                    )}
                  </div>

                  {/* Customer-specific stats */}
                  {(userType === "customer" || userType === "user") && (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Service Requests</p>
                        <p className="font-bold text-blue-700 text-lg">{profileStats.service_requests || 0}</p>
                      </div>
                      
                      <button className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left">
                        <History className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">Service Request History</span>
                      </button>
                      
                      <button className="w-full flex items-center gap-2 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-left">
                        <Download className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700">Download Invoices</span>
                      </button>
                    </>
                  )}

                  {/* Associate & Business specific stats */}
                  {(userType === "associate" || userType === "business") && (
                    <>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Visits Completed</p>
                        <p className="font-bold text-green-700 text-lg">{profileStats.visits_completed || 0}</p>
                      </div>
                      
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
                        <p className="font-bold text-purple-700 text-lg">{formatCurrency(profileStats.total_earning || 0)}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-500">Highest</p>
                          <p className="font-semibold text-blue-700 text-sm">{formatCurrency(profileStats.highest_earning || 0)}</p>
                        </div>
                        <div className="p-2 bg-orange-50 rounded-lg">
                          <p className="text-xs text-gray-500">Average</p>
                          <p className="font-semibold text-orange-700 text-sm">{formatCurrency(profileStats.average_earning || 0)}</p>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-teal-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Max Range Serviced</p>
                        <p className="font-bold text-teal-700">{profileStats.max_range_serviced || 0} km</p>
                      </div>
                      
                      <button className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left">
                        <History className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">Visit History</span>
                      </button>
                      
                      <button className="w-full flex items-center gap-2 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-left">
                        <Download className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700">Download Invoices</span>
                      </button>
                    </>
                  )}

                  {/* Joining Date - Common for all */}
                  <div className="p-3 bg-gray-50 rounded-lg border-t border-gray-100 mt-4">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Member Since
                    </p>
                    <p className="font-semibold text-gray-700">{formatDate(profileStats.joining_date || user.created_at)}</p>
                  </div>
                </div>
              )}
              
              {!profileStats && (
                <div className="text-center py-4">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading stats...</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Content - Tabs and Posts */}
          <div className="flex-1">
            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-md p-2 flex gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                      activeTab === tab.id
                        ? "bg-blue-700 text-white"
                        : "text-gray-600 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Posts List */}
            <div className="mt-6 space-y-4">
              {/* For Service Providers - Show Available Jobs */}
              {isServiceProvider && activeTab === "sub-home" && (
                <>
                  {availablePosts.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                      <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No service requests available in {user.city}</p>
                      <p className="text-sm text-gray-400 mt-1">Check back later for new requests</p>
                    </div>
                  ) : (
                    availablePosts.map((post) => {
                      const hasQuoted = post.quotations?.some(q => q.associate_id === user.id);
                      return (
                        <div key={post.id} className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
                          {/* Post ID Row */}
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded">
                              ID: {post.id?.slice(0, 8).toUpperCase()}
                            </span>
                            <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              {post.quotations?.length || 0} Quotes
                            </span>
                          </div>

                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{post.service_type || "General"}</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  post.urgency === "urgent" ? "bg-red-100 text-red-700" :
                                  post.urgency === "flexible" ? "bg-green-100 text-green-700" :
                                  "bg-gray-100 text-gray-700"
                                }`}>
                                  {post.urgency === "urgent" ? "🔥 Urgent" : post.urgency === "flexible" ? "Flexible" : "Normal"}
                                </span>
                              </div>
                              <h3 className="font-bold text-blue-900 mt-2">{post.title}</h3>
                              <p className="text-gray-600 text-sm mt-1 line-clamp-2">{post.description}</p>
                            </div>
                          </div>
                          
                          {/* Post Details */}
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <div className="flex items-center gap-1 text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span>{post.location || post.city}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>{post.preferred_date || "Flexible"}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>{post.preferred_time === "morning" ? "Morning" : post.preferred_time === "afternoon" ? "Afternoon" : post.preferred_time === "evening" ? "Evening" : "Anytime"}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                              <User className="w-4 h-4" />
                              <span>{post.contact_name}</span>
                            </div>
                          </div>

                          {/* Photos Preview */}
                          {post.photos && post.photos.length > 0 && (
                            <div className="flex gap-2 mb-3 overflow-x-auto">
                              {post.photos.slice(0, 3).map((photo, idx) => (
                                <img key={idx} src={photo} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                              ))}
                              {post.photos.length > 3 && (
                                <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                  +{post.photos.length - 3}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-3 border-t">
                            {hasQuoted ? (
                              <div className="flex-1 py-2 bg-green-50 text-green-700 rounded-lg text-center text-sm font-medium">
                                <CheckCircle className="w-4 h-4 inline mr-1" />
                                Quotation Submitted
                              </div>
                            ) : (
                              <button
                                onClick={() => handleOpenQuoteModal(post)}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                              >
                                Send Quotation
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {/* For Customers - Show Their Own Posts */}
              {!isServiceProvider && activeTab === "sub-home" && (
                <>
                  {/* Create Enquiry Button */}
                  <button
                    onClick={() => setShowCreatePost(true)}
                    className="w-full py-4 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors mb-4"
                    data-testid="create-enquiry-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Create New Enquiry
                  </button>

                  {posts.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                      <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No active service requests</p>
                      <p className="text-sm text-gray-400 mt-1">Create a new request to get quotes from service providers</p>
                    </div>
                  ) : (
                    posts.map((post) => (
                      <div key={post.id} className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
                        {/* Post ID and Status Row */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded">
                            ID: {post.id?.slice(0, 8).toUpperCase()}
                          </span>
                          <div className="flex items-center gap-2">
                            {/* Urgency Tag */}
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              post.urgency === "urgent" ? "bg-red-100 text-red-700" :
                              post.urgency === "flexible" ? "bg-green-100 text-green-700" :
                              "bg-blue-100 text-blue-700"
                            }`}>
                              {post.urgency === "urgent" ? "🔥 Urgent" : post.urgency === "flexible" ? "Flexible" : "Normal"}
                            </span>
                            {/* Quote Count */}
                            <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              {post.quotations?.length || 0} Quotes
                            </span>
                            {/* Status */}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              post.status === "quoted" ? "bg-orange-100 text-orange-700" :
                              post.status === "scheduled" ? "bg-green-100 text-green-700" :
                              "bg-blue-100 text-blue-700"
                            }`}>
                              {post.status === "quoted" ? "Has Quotes" : post.status === "scheduled" ? "Scheduled" : "Active"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{post.service_type || "General"}</span>
                            </div>
                            <h3 className="font-bold text-blue-900">{post.title}</h3>
                            <p className="text-gray-600 text-sm mt-1">{post.description}</p>
                          </div>
                          {/* Edit & Delete Buttons */}
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                setSelectedPost(post);
                                setShowEditPost(true);
                              }}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit Post"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm("Are you sure you want to delete this enquiry?")) {
                                  try {
                                    await axios.delete(`${API}/posts/${post.id}`);
                                    setRefreshKey(prev => prev + 1);
                                  } catch (e) {
                                    alert("Failed to delete post");
                                  }
                                }
                              }}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Post"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {post.location || post.city}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {post.preferred_date || "Flexible"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.preferred_time || "Anytime"}
                          </span>
                        </div>

                        {/* View Quotations Button if has quotes */}
                        {post.quotations && post.quotations.length > 0 && (
                          <button
                            onClick={() => setActiveTab("followups")}
                            className="w-full mt-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors"
                          >
                            View {post.quotations.length} Quotation{post.quotations.length > 1 ? 's' : ''}
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </>
              )}

              {/* Followups Tab - For Customers: Show posts with quotations to review */}
              {!isServiceProvider && activeTab === "followups" && (
                <>
                  {posts.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                      <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No quotations to review</p>
                    </div>
                  ) : (
                    posts.map((post) => (
                      <div key={post.id} className="bg-white rounded-xl shadow-md p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{post.service_type || "General"}</span>
                            <h3 className="font-bold text-blue-900 mt-2">{post.title}</h3>
                            <p className="text-gray-600 text-sm mt-1">{post.description}</p>
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            {post.quotations?.length || 0} Quotes
                          </span>
                        </div>

                        {/* Quotations List */}
                        <div className="space-y-3 mt-4">
                          <h4 className="font-medium text-gray-700">Received Quotations:</h4>
                          {post.quotations?.map((quote) => (
                            <div key={quote.id} className={`p-4 rounded-lg border-2 ${quote.status === "accepted" ? "border-green-300 bg-green-50" : "border-gray-200"}`}>
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-gray-900">{quote.associate_name}</p>
                                  <p className="text-sm text-gray-500">{quote.associate_type === "business" ? "Business Partner" : "Service Associate"}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-blue-700">{formatCurrency(quote.min_estimate)} - {formatCurrency(quote.max_estimate)}</p>
                                  <p className="text-xs text-gray-500">Site Visit: {formatCurrency(quote.site_visit_charge)}</p>
                                </div>
                              </div>
                              
                              {/* Price Breakup */}
                              {quote.price_breakup && quote.price_breakup.length > 0 && (
                                <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                                  <p className="font-medium text-gray-700 mb-1">Price Breakup:</p>
                                  {quote.price_breakup.map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-gray-600">
                                      <span>{item.item}</span>
                                      <span>{formatCurrency(item.amount)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {quote.notes && (
                                <p className="text-sm text-gray-600 mt-2 italic">"{quote.notes}"</p>
                              )}

                              {/* Accept/Reject Buttons */}
                              {quote.status === "pending" && (
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={() => handleAcceptQuotation(post.id, quote.id)}
                                    className="flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                                  >
                                    Accept Quote
                                  </button>
                                </div>
                              )}
                              {quote.status === "accepted" && (
                                <div className="mt-2 text-green-700 font-medium text-sm flex items-center gap-1">
                                  <CheckCircle className="w-4 h-4" />
                                  Accepted
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}

              {/* For Service Providers - Quoted posts (Followups) */}
              {isServiceProvider && activeTab === "followups" && (
                <>
                  {posts.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                      <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No pending quotations</p>
                    </div>
                  ) : (
                    posts.map((post) => {
                      const myQuote = post.quotations?.find(q => q.associate_id === user.id);
                      return (
                        <div key={post.id} className="bg-white rounded-xl shadow-md p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{post.service_type || "General"}</span>
                              <h3 className="font-bold text-blue-900 mt-2">{post.title}</h3>
                              <p className="text-gray-600 text-sm mt-1">{post.description}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              myQuote?.status === "accepted" ? "bg-green-100 text-green-700" :
                              myQuote?.status === "rejected" ? "bg-red-100 text-red-700" :
                              "bg-yellow-100 text-yellow-700"
                            }`}>
                              {myQuote?.status === "accepted" ? "Accepted" : myQuote?.status === "rejected" ? "Rejected" : "Pending"}
                            </span>
                          </div>
                          {myQuote && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                              <p className="text-sm text-gray-700">Your Quote: <span className="font-bold text-blue-700">{formatCurrency(myQuote.min_estimate)} - {formatCurrency(myQuote.max_estimate)}</span></p>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {/* Scheduled Tab - Both user types */}
              {activeTab === "scheduled" && (
                <>
                  {posts.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No scheduled visits</p>
                    </div>
                  ) : (
                    posts.map((post) => {
                      const acceptedQuote = post.quotations?.find(q => q.status === "accepted");
                      return (
                        <ScheduledServiceCard
                          key={post.id}
                          post={post}
                          acceptedQuote={acceptedQuote}
                          isServiceProvider={isServiceProvider}
                          currentUser={user}
                          userType={userType}
                          formatCurrency={formatCurrency}
                        />
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Sidebar - Updates */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-md p-5 sticky top-24">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Updates
                </span>
                {updates.filter(u => !u.read).length > 0 && (
                  <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                    {updates.filter(u => !u.read).length} new
                  </span>
                )}
              </h3>
              
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {updates.length === 0 ? (
                  <div className="text-center py-6">
                    <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No updates yet</p>
                  </div>
                ) : (
                  updates.map((update) => {
                    // Get icon and colors based on update type
                    const getUpdateStyle = (type) => {
                      switch(type) {
                        case "accepted":
                          return { icon: CheckCircle, bg: "bg-green-50", border: "border-green-200", iconColor: "text-green-500" };
                        case "completed":
                          return { icon: CheckCircle2, bg: "bg-blue-50", border: "border-blue-200", iconColor: "text-blue-500" };
                        case "cancelled":
                          return { icon: XCircle, bg: "bg-red-50", border: "border-red-200", iconColor: "text-red-500" };
                        case "message":
                          return { icon: MessageCircle, bg: "bg-purple-50", border: "border-purple-200", iconColor: "text-purple-500" };
                        case "new_request":
                          return { icon: AlertCircle, bg: "bg-orange-50", border: "border-orange-200", iconColor: "text-orange-500" };
                        default:
                          return { icon: Bell, bg: "bg-gray-50", border: "border-gray-200", iconColor: "text-gray-500" };
                      }
                    };
                    
                    const style = getUpdateStyle(update.type);
                    const UpdateIcon = style.icon;
                    
                    // Format time ago
                    const getTimeAgo = (timestamp) => {
                      const now = new Date();
                      const time = new Date(timestamp);
                      const diff = Math.floor((now - time) / 1000);
                      
                      if (diff < 60) return "Just now";
                      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
                      return time.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                    };
                    
                    return (
                      <div 
                        key={update.id} 
                        className={`p-3 rounded-lg border ${style.bg} ${style.border} ${!update.read ? 'ring-2 ring-blue-300' : ''} cursor-pointer hover:shadow-sm transition-shadow`}
                      >
                        <div className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                            <UpdateIcon className={`w-4 h-4 ${style.iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-medium text-sm text-gray-900 truncate">{update.title}</p>
                              {!update.read && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{update.message}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{update.service}</span>
                              <span className="text-xs text-gray-400">{getTimeAgo(update.time)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              {updates.length > 0 && (
                <button className="w-full mt-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors">
                  View All Updates
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileModal 
          user={user} 
          userType={userType} 
          onClose={() => setShowEditModal(false)}
          onUpdate={updateUser}
        />
      )}

      {/* Quotation Modal for Service Providers */}
      {showQuoteModal && selectedPost && (
        <QuotationModal
          post={selectedPost}
          user={user}
          userType={userType}
          onClose={() => {
            setShowQuoteModal(false);
            setSelectedPost(null);
          }}
          onSuccess={() => {
            setShowQuoteModal(false);
            setSelectedPost(null);
            setActiveTab("followups");
          }}
        />
      )}

      {/* Create Enquiry Modal for Customers */}
      {showCreatePost && (
        <CreatePostModal onClose={() => {
          setShowCreatePost(false);
          // Refresh posts after creating
          setRefreshKey(prev => prev + 1);
        }} />
      )}

      {/* Edit Post Modal for Customers */}
      {showEditPost && selectedPost && (
        <EditPostModal 
          post={selectedPost}
          onClose={() => {
            setShowEditPost(false);
            setSelectedPost(null);
          }}
          onSuccess={() => {
            setShowEditPost(false);
            setSelectedPost(null);
            setRefreshKey(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
};


// Edit Post Modal - For Customers to edit their posts
const EditPostModal = ({ post, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: post.title || "",
    description: post.description || "",
    service_type: post.service_type || "",
    location: post.location || "",
    city: post.city || "",
    preferred_date: post.preferred_date || "",
    preferred_time: post.preferred_time || "",
    urgency: post.urgency || "normal",
    contact_name: post.contact_name || "",
    contact_phone: post.contact_phone || "",
  });
  const [loading, setLoading] = useState(false);

  const serviceTypes = [
    "Plumbing", "Electrical", "Carpentry", "Painting", "AC Repair", 
    "Deep Cleaning", "Pest Control", "Appliance Repair", "Masonry",
    "Roofing", "Flooring", "Interior Design", "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/posts/${post.id}`, formData);
      alert("Post updated successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error updating post:", error);
      alert("Failed to update post");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-xl font-bold">Edit Enquiry</h2>
            <p className="text-xs text-blue-200">ID: {post.id?.slice(0, 8).toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-blue-600 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
            <select
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 bg-white"
            >
              <option value="">Select service type</option>
              {serviceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <input
            type="text"
            placeholder="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500"
          />

          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 resize-none"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
            <div className="flex gap-2">
              {["urgent", "normal", "flexible"].map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setFormData({ ...formData, urgency: opt })}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    formData.urgency === opt
                      ? opt === "urgent" ? "bg-red-500 text-white" 
                        : opt === "normal" ? "bg-blue-500 text-white"
                        : "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {opt === "urgent" ? "🔥 Urgent" : opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date</label>
              <input
                type="date"
                value={formData.preferred_date}
                onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time</label>
              <select
                value={formData.preferred_time}
                onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 bg-white"
              >
                <option value="">Select time</option>
                <option value="morning">Morning (8-12)</option>
                <option value="afternoon">Afternoon (12-4)</option>
                <option value="evening">Evening (4-8)</option>
                <option value="anytime">Anytime</option>
              </select>
            </div>
          </div>

          <input
            type="text"
            placeholder="Location"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
          >
            {loading ? "Updating..." : "Update Enquiry"}
          </button>
        </form>
      </div>
    </div>
  );
};


// Quotation Modal - For Associates/Business to submit quotes
const QuotationModal = ({ post, user, userType, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    site_visit_charge: 50,
    min_estimate: 0,
    max_estimate: 0,
    price_breakup: [
      { item: "Site Visit / Audit", amount: 50 },
    ],
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState({ item: "", amount: 0 });

  // Calculate site visit charge based on distance (demo logic)
  const calculateSiteVisitCharge = () => {
    // In production, calculate actual distance using coordinates
    // For demo: random distance between 1-15 km
    const distance = Math.random() * 15;
    return distance <= 5 ? 50 : distance <= 10 ? 100 : 150;
  };

  useEffect(() => {
    const siteCharge = calculateSiteVisitCharge();
    setFormData(prev => ({
      ...prev,
      site_visit_charge: siteCharge,
      price_breakup: [{ item: "Site Visit / Audit", amount: siteCharge }]
    }));
  }, []);

  const addPriceItem = () => {
    if (newItem.item && newItem.amount > 0) {
      setFormData({
        ...formData,
        price_breakup: [...formData.price_breakup, { ...newItem }]
      });
      setNewItem({ item: "", amount: 0 });
    }
  };

  const removePriceItem = (index) => {
    if (index === 0) return; // Don't remove site visit charge
    setFormData({
      ...formData,
      price_breakup: formData.price_breakup.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    return formData.price_breakup.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.min_estimate <= 0 || formData.max_estimate <= 0) {
      alert("Please enter valid minimum and maximum estimates");
      return;
    }
    if (formData.max_estimate < formData.min_estimate) {
      alert("Maximum estimate cannot be less than minimum estimate");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/posts/${post.id}/quote`, {
        associate_id: user.id,
        associate_name: userType === "business" ? user.business_name : user.name,
        associate_phone: user.phone,
        associate_type: userType,
        site_visit_charge: formData.site_visit_charge,
        min_estimate: formData.min_estimate,
        max_estimate: formData.max_estimate,
        price_breakup: formData.price_breakup,
        notes: formData.notes,
      });
      alert("Quotation submitted successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error submitting quotation:", error);
      alert(error.response?.data?.detail || "Failed to submit quotation");
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-xl font-bold">Submit Quotation</h2>
          <button onClick={onClose} className="p-1 hover:bg-blue-600 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Post Summary */}
        <div className="p-4 bg-blue-50 border-b">
          <p className="text-sm text-gray-500">Service Request</p>
          <h3 className="font-bold text-blue-900">{post.title}</h3>
          <p className="text-sm text-gray-600 mt-1">{post.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{post.location || post.city}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{post.preferred_date || "Flexible"}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Site Visit Charge Info */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <h4 className="font-medium text-yellow-800 mb-2">Site Visit Charge</h4>
            <p className="text-sm text-yellow-700">
              Based on distance: <span className="font-bold">{formatCurrency(formData.site_visit_charge)}</span>
            </p>
            <p className="text-xs text-yellow-600 mt-1">
              ₹50 within 5 KM | ₹100 within 10 KM | ₹150 beyond 10 KM
            </p>
          </div>

          {/* Price Breakup */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Price Breakup (Tentative)</h4>
            <div className="space-y-2">
              {formData.price_breakup.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <span className="flex-1 text-sm">{item.item}</span>
                  <span className="font-medium text-blue-700">{formatCurrency(item.amount)}</span>
                  {idx > 0 && (
                    <button type="button" onClick={() => removePriceItem(idx)} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add New Item */}
            <div className="flex gap-2 mt-3">
              <input
                type="text"
                placeholder="Item name (e.g., Material, Labor)"
                value={newItem.item}
                onChange={(e) => setNewItem({ ...newItem, item: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <input
                type="number"
                placeholder="Amount"
                value={newItem.amount || ""}
                onChange={(e) => setNewItem({ ...newItem, amount: parseInt(e.target.value) || 0 })}
                className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={addPriceItem}
                className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-3 p-3 bg-blue-50 rounded-lg flex justify-between">
              <span className="font-medium">Estimated Total:</span>
              <span className="font-bold text-blue-700">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>

          {/* Min/Max Estimates */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Final Estimate Range *</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Minimum (₹)</label>
                <input
                  type="number"
                  value={formData.min_estimate || ""}
                  onChange={(e) => setFormData({ ...formData, min_estimate: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500"
                  placeholder="Min amount"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Maximum (₹)</label>
                <input
                  type="number"
                  value={formData.max_estimate || ""}
                  onChange={(e) => setFormData({ ...formData, max_estimate: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500"
                  placeholder="Max amount"
                  required
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 resize-none"
              placeholder="Any additional information for the customer..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
          >
            {loading ? "Submitting..." : "Submit Quotation"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Edit Profile Modal - Handles all 3 user types
const EditProfileModal = ({ user, userType, onClose, onUpdate }) => {
  const [formData, setFormData] = useState(user);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [showSavedPopup, setShowSavedPopup] = useState(false);

  const serviceOptions = [
    "Plumbing", "Electrical", "Carpentry", "Painting", "Cleaning",
    "AC Repair", "Appliance Repair", "Moving & Packing", "Gardening", "Construction", "Mechanic", "Salon", "Deep Cleaning", "Other"
  ];

  const handleServiceToggle = (service) => {
    const currentServices = formData.services || [];
    if (currentServices.includes(service)) {
      setFormData({ ...formData, services: currentServices.filter(s => s !== service) });
    } else {
      setFormData({ ...formData, services: [...currentServices, service] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let endpoint;
      if (userType === "business") {
        endpoint = `${API}/businesses/by-phone/${user.phone}`;
      } else if (userType === "associate") {
        endpoint = `${API}/associates/by-phone/${user.phone}`;
      } else {
        endpoint = `${API}/auth/user/${user.id}`;
      }
      
      const res = await axios.put(endpoint, formData);
      const updatedData = res.data.user || res.data.associate || res.data.business;
      onUpdate(updatedData);
      
      // Show saved popup and close after 0.5 seconds
      setShowSavedPopup(true);
      setTimeout(() => {
        setShowSavedPopup(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile");
    }
    setLoading(false);
  };

  const sections = userType === "customer" 
    ? [{ id: "basic", label: "Basic Info" }]
    : userType === "associate"
    ? [
        { id: "basic", label: "Basic Info" },
        { id: "services", label: "Services" },
        { id: "documents", label: "Documents" },
        { id: "bank", label: "Bank Details" }
      ]
    : [
        { id: "basic", label: "Basic Info" },
        { id: "business", label: "Business Details" },
        { id: "documents", label: "Documents" },
        { id: "bank", label: "Bank Details" }
      ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-profile-modal">
      {/* Saved Popup */}
      {showSavedPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
          <div className="bg-green-500 text-white px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-pulse">
            <CheckCircle2 className="w-6 h-6" />
            <span className="text-lg font-bold">Saved!</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="sticky top-0 bg-blue-700 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Edit Profile</h2>
          <button onClick={onClose} className="p-1 hover:bg-blue-600 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="border-b border-gray-200 px-6 pt-4 flex gap-2 overflow-x-auto">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeSection === section.id
                  ? "bg-blue-50 text-blue-700 border-b-2 border-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Basic Info Section - All Users */}
          {activeSection === "basic" && (
            <div className="space-y-4">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Basic Information
              </h3>
              
              {userType === "business" ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                    <input
                      type="text"
                      value={formData.business_name || ""}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                      placeholder="Enter business name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Owner Name *</label>
                    <input
                      type="text"
                      value={formData.owner_name || ""}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                      placeholder="Enter owner name"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                <CitySelect 
                  value={formData.city || ""}
                  onChange={(city) => setFormData({ ...formData, city })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {userType === "business" ? "Primary Contact *" : "Contact Number"}
                </label>
                <input
                  type="tel"
                  value={userType === "business" ? (formData.primary_contact || formData.phone || "") : (formData.phone || "")}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    [userType === "business" ? "primary_contact" : "phone"]: e.target.value 
                  })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="Enter contact number"
                />
              </div>

              {userType === "business" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Contact</label>
                  <input
                    type="tel"
                    value={formData.secondary_contact || ""}
                    onChange={(e) => setFormData({ ...formData, secondary_contact: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                    placeholder="Enter secondary contact"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Address</label>
                <textarea
                  value={formData.full_address || ""}
                  onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all resize-none placeholder:text-gray-500"
                  placeholder="Enter complete address"
                />
              </div>

              <FileUploadField
                label="Profile Photo"
                value={formData.photo_url || ""}
                onChange={(url) => setFormData({ ...formData, photo_url: url })}
                accept="image/*"
                placeholder="Or paste image URL here"
              />
            </div>
          )}

          {/* Services Section - Associates Only */}
          {activeSection === "services" && userType === "associate" && (
            <div className="space-y-4">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Services & Experience
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Services Offered</label>
                <div className="flex flex-wrap gap-2">
                  {serviceOptions.map((service) => (
                    <button
                      key={service}
                      type="button"
                      onClick={() => handleServiceToggle(service)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        (formData.services || []).includes(service)
                          ? "bg-blue-700 text-white"
                          : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
                <input
                  type="text"
                  value={formData.experience || ""}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="e.g., 5 years"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Range (KM)</label>
                <input
                  type="number"
                  value={formData.service_range_km || ""}
                  onChange={(e) => setFormData({ ...formData, service_range_km: parseInt(e.target.value) || null })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="e.g., 10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">About Your Services</label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all resize-none placeholder:text-gray-500"
                  placeholder="Describe your skills and experience"
                />
              </div>
            </div>
          )}

          {/* Business Details Section - Business Only */}
          {activeSection === "business" && userType === "business" && (
            <div className="space-y-4">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Business Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Category</label>
                <select
                  value={formData.category || ""}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all"
                >
                  <option value="">Select category</option>
                  {["Retail", "Restaurant", "Healthcare", "Education", "Real Estate", "Manufacturing", "IT Services", "Construction", "Transportation", "Electrical", "Plumbing", "Other"].map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year of Establishment</label>
                <input
                  type="number"
                  value={formData.year_of_establishment || ""}
                  onChange={(e) => setFormData({ ...formData, year_of_establishment: parseInt(e.target.value) || null })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="e.g., 2015"
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Range (KM)</label>
                <input
                  type="number"
                  value={formData.service_range_km || ""}
                  onChange={(e) => setFormData({ ...formData, service_range_km: parseInt(e.target.value) || null })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="e.g., 25"
                />
              </div>

              {/* Location Capture - Business Partners Only */}
              <LocationCaptureField
                label="Capture Business Location"
                value={formData.captured_location || ""}
                onChange={(loc) => setFormData({ ...formData, captured_location: loc })}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps Link (Optional)</label>
                <input
                  type="text"
                  value={formData.map_pin_location || ""}
                  onChange={(e) => setFormData({ ...formData, map_pin_location: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="Or paste Google Maps link"
                />
                <p className="text-xs text-gray-400 mt-1">Alternative: Paste a Google Maps share link</p>
              </div>

              <FileUploadField
                label="Shop Photo"
                value={formData.shop_photo || ""}
                onChange={(url) => setFormData({ ...formData, shop_photo: url })}
                accept="image/*"
                placeholder="Or paste shop photo URL here"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Description</label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all resize-none placeholder:text-gray-500"
                  placeholder="Describe your business"
                />
              </div>
            </div>
          )}

          {/* Documents Section - Associates & Business */}
          {activeSection === "documents" && (userType === "associate" || userType === "business") && (
            <div className="space-y-4">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents & Verification
              </h3>

              {userType === "associate" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Aadhar Number</label>
                    <input
                      type="text"
                      value={formData.aadhar_number || ""}
                      onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                      placeholder="Enter 12-digit Aadhar number"
                      maxLength={12}
                    />
                  </div>

                  <FileUploadField
                    label="Aadhar Card Photo"
                    value={formData.aadhar_photo || ""}
                    onChange={(url) => setFormData({ ...formData, aadhar_photo: url })}
                    accept="image/*,.pdf"
                    placeholder="Or paste Aadhar image URL"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number</label>
                    <input
                      type="text"
                      value={formData.pan_number || ""}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                      placeholder="Enter PAN number"
                      maxLength={10}
                    />
                  </div>

                  <FileUploadField
                    label="PAN Card Photo"
                    value={formData.pan_photo || ""}
                    onChange={(url) => setFormData({ ...formData, pan_photo: url })}
                    accept="image/*,.pdf"
                    placeholder="Or paste PAN image URL"
                  />
                </>
              )}

              {userType === "business" && (
                <FileUploadField
                  label="Shop Registration Certificate"
                  value={formData.shop_registration_certificate || ""}
                  onChange={(url) => setFormData({ ...formData, shop_registration_certificate: url })}
                  accept="image/*,.pdf"
                  placeholder="Or paste certificate URL"
                />
              )}
            </div>
          )}

          {/* Bank Details Section - Associates & Business */}
          {activeSection === "bank" && (userType === "associate" || userType === "business") && (
            <div className="space-y-4">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Bank Account Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <input
                  type="text"
                  value={formData.bank_name || ""}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="Enter bank name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  value={formData.bank_account_number || ""}
                  onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="Enter account number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                <input
                  type="text"
                  value={formData.bank_ifsc || ""}
                  onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 transition-all placeholder:text-gray-500"
                  placeholder="Enter IFSC code"
                  maxLength={11}
                />
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Register Associate Page
const RegisterAssociatePage = () => {
  usePageTitle("Register as Associate");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    services: [],
    experience: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const serviceOptions = [
    "Plumbing", "Electrical", "Carpentry", "Painting", "Cleaning",
    "AC Repair", "Appliance Repair", "Moving & Packing", "Gardening", "Other"
  ];

  const handleServiceToggle = (service) => {
    if (formData.services.includes(service)) {
      setFormData({ ...formData, services: formData.services.filter(s => s !== service) });
    } else {
      setFormData({ ...formData, services: [...formData.services, service] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.city) {
      alert("Please fill all required fields");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/associates`, formData);
      setSuccess(true);
    } catch (error) {
      alert("Registration failed. Please try again.");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8 flex items-center justify-center" data-testid="register-success">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-blue-900 mb-2">Registration Successful!</h2>
          <p className="text-gray-600 mb-6">Thank you for registering as an associate. We'll verify your details and get back to you soon.</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8" data-testid="register-associate-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-2xl font-bold text-blue-900 mb-2 text-center">Register as Associate</h1>
          <p className="text-gray-600 text-center mb-8">Join our network of trusted service providers</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="associate-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter your phone number"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="associate-phone-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="associate-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
              <CitySelect 
                value={formData.city}
                onChange={(city) => setFormData({ ...formData, city })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Services Offered</label>
              <div className="flex flex-wrap gap-2">
                {serviceOptions.map((service) => (
                  <button
                    key={service}
                    type="button"
                    onClick={() => handleServiceToggle(service)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      formData.services.includes(service)
                        ? "bg-blue-700 text-white"
                        : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                    }`}
                    data-testid={`service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
              <input
                type="text"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                placeholder="e.g., 5 years"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="associate-experience-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">About You</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell us about your skills and experience"
                rows={4}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none placeholder:text-gray-500"
                data-testid="associate-description-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
              data-testid="submit-associate-button"
            >
              {loading ? "Registering..." : "REGISTER AS ASSOCIATE"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Partner Page
const PartnerPage = () => {
  usePageTitle("Partner with Us");
  const [formData, setFormData] = useState({
    business_name: "",
    owner_name: "",
    phone: "",
    email: "",
    city: "",
    category: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const categories = [
    "Retail", "Restaurant", "Healthcare", "Education", "Real Estate",
    "Manufacturing", "IT Services", "Construction", "Transportation", "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name || !formData.owner_name || !formData.phone || !formData.city || !formData.category) {
      alert("Please fill all required fields");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/businesses`, formData);
      setSuccess(true);
    } catch (error) {
      alert("Registration failed. Please try again.");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-blue-900 mb-2">Partnership Request Sent!</h2>
          <p className="text-gray-600 mb-6">Thank you for your interest. Our team will contact you soon.</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8" data-testid="partner-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-2xl font-bold text-blue-900 mb-2 text-center">Partner With Us</h1>
          <p className="text-gray-600 text-center mb-8">Register your business and expand your reach</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="Enter business name"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="business-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Owner Name *</label>
              <input
                type="text"
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                placeholder="Enter owner name"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="owner-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="business-phone-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="business-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
              <CitySelect 
                value={formData.city}
                onChange={(city) => setFormData({ ...formData, city })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-700"
                data-testid="business-category-select"
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell us about your business"
                rows={4}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none placeholder:text-gray-500"
                data-testid="business-description-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
              data-testid="submit-partner-button"
            >
              {loading ? "Submitting..." : "SUBMIT PARTNERSHIP REQUEST"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  usePageTitle("Contact Us");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      alert("Please fill all required fields");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
    } catch (error) {
      alert("Failed to send message. Please try again.");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-blue-900 mb-2">Message Sent!</h2>
          <p className="text-gray-600 mb-6">We'll get back to you as soon as possible.</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8" data-testid="contact-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-2xl font-bold text-blue-900 mb-2 text-center">Contact Us</h1>
          <p className="text-gray-600 text-center mb-8">Have questions? We'd love to hear from you.</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="contact-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Your email"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="contact-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Your phone number"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder:text-gray-500"
                data-testid="contact-phone-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Your message"
                rows={5}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none placeholder:text-gray-500"
                data-testid="contact-message-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
              data-testid="submit-contact-button"
            >
              {loading ? "Sending..." : "SEND MESSAGE"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Hire Associate Page (For Customers)
const HireAssociatePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [associates, setAssociates] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedService, setSelectedService] = useState("");

  const serviceOptions = [
    "Plumbing", "Electrical", "Carpentry", "Painting", "Cleaning",
    "AC Repair", "Appliance Repair", "Moving & Packing", "Gardening", "Construction", "Mechanic", "Salon"
  ];

  useEffect(() => {
    const fetchAssociates = async () => {
      try {
        let url = `${API}/associates`;
        const params = [];
        if (selectedCity) params.push(`city=${selectedCity}`);
        if (selectedService) params.push(`service=${selectedService}`);
        if (params.length > 0) url += `?${params.join("&")}`;
        
        const res = await axios.get(url);
        setAssociates(res.data);
      } catch (e) {
        console.error("Error fetching associates:", e);
      }
    };
    fetchAssociates();
  }, [selectedCity, selectedService]);

  const handleHireClick = () => {
    if (!isAuthenticated) {
      navigate("/login?redirect=hire");
    } else {
      navigate("/profile");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8" data-testid="hire-associate-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Hire Verified Associates</h1>
          <p className="text-blue-100 text-lg">
            Find trusted professionals for all your service needs
          </p>
          {!isAuthenticated && (
            <button
              onClick={() => navigate("/login")}
              className="mt-6 px-8 py-3 bg-white text-blue-700 font-bold rounded-full hover:bg-blue-50 transition-colors"
              data-testid="user-signup-button"
            >
              Sign Up as User
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
          <h2 className="text-lg font-bold text-blue-900 mb-4">Find Associates</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select City</label>
              <CitySelect value={selectedCity} onChange={setSelectedCity} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Service</label>
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                data-testid="service-filter"
              >
                <option value="">All Services</option>
                {serviceOptions.map((service) => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Associates List */}
        <h2 className="text-2xl font-bold text-blue-900 mb-6">Available Associates ({associates.length})</h2>
        
        {associates.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <p className="text-gray-500">No associates found. Try different filters or check back later.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {associates.map((assoc) => (
              <div key={assoc.id} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-900">{assoc.name}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {assoc.city}
                    </p>
                  </div>
                  {assoc.verified && (
                    <span className="ml-auto px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      Verified
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {assoc.services?.slice(0, 4).map((service, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                      {service}
                    </span>
                  ))}
                </div>
                
                {assoc.experience && (
                  <p className="text-sm text-gray-600 mb-4">
                    <span className="font-medium">Experience:</span> {assoc.experience}
                  </p>
                )}
                
                <button
                  onClick={handleHireClick}
                  className="w-full py-3 bg-blue-700 text-white font-medium rounded-xl hover:bg-blue-800 transition-colors"
                  data-testid={`hire-${assoc.id}`}
                >
                  Contact to Hire
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// B2B Services Page
const B2BServicesPage = () => {
  usePageTitle("B2B Services");
  const navigate = useNavigate();

  const workforceServices = [
    "Construction & Infrastructure",
    "Manufacturing & Engineering",
    "Facility Management",
    "Logistics & Warehousing",
    "Hospitality & Maintenance",
    "Agriculture & Seasonal Work"
  ];

  const contractDurations = ["15 Days", "1 Month", "3 Months", "6 Months", "8 Months"];

  const trustFeatures = [
    { icon: "✅", title: "No Commissions from Workers", desc: "100% of wages go to workers — no hidden cuts." },
    { icon: "🧾", title: "Transparent Pricing", desc: "Affordable, contract-based, and clearly defined terms." },
    { icon: "🧍‍♂️", title: "Verified Workforce", desc: "Every worker is background-checked and identity-verified." },
    { icon: "🛡️", title: "Insurance & Safety", desc: "Workers are insured during their tenure." },
    { icon: "🏭", title: "Certified Experience", desc: "Each worker gets certified for employment history and performance." },
    { icon: "🔄", title: "Guaranteed Continuity", desc: "We ensure 12 months of regular work across industries for our workforce." },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8" data-testid="b2b-services-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-black mb-6 leading-tight">
            Reliable Workforce Solutions for Every Industry
          </h1>
          <p className="text-xl text-blue-100 leading-relaxed">
            Connecting organizations with verified skilled, semi-skilled,<br />
            and unskilled workers — affordably, efficiently,<br />
            and transparently
          </p>
        </div>
      </div>

      {/* About Section */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At <span className="font-bold text-blue-700">Milanam Workforce Solutions</span>, we bridge the gap between organizations in need of reliable manpower and skilled individuals seeking fair employment opportunities.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Our mission is simple — to empower industries with dependable human resources while ensuring ethical treatment, fair pay, and year-round opportunities for workers.
          </p>
          
          <h3 className="text-xl font-bold text-blue-900 mb-4">Why We Exist:</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="text-blue-600 font-bold">•</span>
              Organizations often struggle to find trustworthy labour suppliers.
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-600 font-bold">•</span>
              Workers face exploitation and irregular work.
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-600 font-bold">•</span>
              We solve both problems with transparency and verified contracting.
            </li>
          </ul>
        </div>

        {/* What We Offer */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">What We Offer</h2>
          <p className="text-gray-700 mb-6">
            We supply skilled, semi-skilled, and unskilled labour across various domains:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {workforceServices.map((service, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {idx + 1}
                </div>
                <span className="text-gray-800 font-medium">{service}</span>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-blue-900 mb-4">Contract Durations Available:</h3>
          <div className="flex flex-wrap gap-3 mb-8">
            {contractDurations.map((duration, idx) => (
              <span key={idx} className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-medium">
                {duration}
              </span>
            ))}
          </div>

          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-blue-900 mb-3">Contract Extension Policy:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                Extensions must be informed 15 days prior to expiry.
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                No additional base charges for extension.
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                If extended, 5% of per-day wage per worker applies as an extension handling charge.
              </li>
            </ul>
          </div>
        </div>

        {/* Why Organizations Trust Milanam */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Why Organizations Trust Milanam</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {trustFeatures.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-white rounded-xl">
                <span className="text-2xl">{feature.icon}</span>
                <div>
                  <h4 className="font-bold text-blue-900">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-gray-700 text-center italic">
            Our ethical approach ensures organizations get reliable manpower while workers enjoy fair treatment and job security.
          </p>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-2xl shadow-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Let's Work Together</h2>
          <p className="text-blue-100 mb-6">
            Looking to hire skilled or unskilled labour for your project or organization?
          </p>
          
          <div className="flex flex-col md:flex-row justify-center gap-6 mb-6">
            <a href="https://wa.me/919759266324" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-lg">
              <Phone className="w-5 h-5" />
              <span>WhatsApp: +91-9759266324</span>
            </a>
            <a href="mailto:shubham@milanam.com" className="flex items-center justify-center gap-2 text-lg">
              <Mail className="w-5 h-5" />
              <span>Email: shubham@milanam.com</span>
            </a>
          </div>
          
          <p className="text-blue-200 text-sm mb-8">
            We'll share quotations and worker profiles based on your requirements within 72 hours.
          </p>

          <button
            onClick={() => navigate("/contact")}
            className="px-8 py-4 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            data-testid="b2b-contact-button"
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  usePageTitle("Admin Dashboard");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      fetchDashboardData();
    } catch (err) {
      setError("Invalid admin password. Use: admin123");
    }
    setLoading(false);
  };

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setDashboardData(res.data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`);
      fetchDashboardData();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  // Simple Bar Chart Component
  const BarChart = ({ data, title, color = "blue" }) => {
    const maxValue = Math.max(...data.map(d => d.total), 1);
    return (
      <div className="bg-white rounded-xl shadow-md p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
        <div className="flex items-end gap-2 h-32">
          {data.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div 
                className={`w-full bg-${color}-500 rounded-t transition-all hover:bg-${color}-600`}
                style={{ height: `${(d.total / maxValue) * 100}%`, minHeight: d.total > 0 ? "4px" : "0" }}
                title={`${d.day}: ${d.total} registrations`}
              ></div>
              <span className="text-xs text-gray-500 mt-1">{d.day}</span>
              <span className="text-xs font-semibold text-gray-700">{d.total}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Donut/Pie chart component
  const DonutChart = ({ data, title, colors }) => {
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    let currentAngle = 0;
    const segments = Object.entries(data).map(([key, value], i) => {
      const percentage = total > 0 ? (value / total) * 100 : 0;
      const angle = (percentage / 100) * 360;
      const segment = { key, value, percentage, startAngle: currentAngle, endAngle: currentAngle + angle, color: colors[i % colors.length] };
      currentAngle += angle;
      return segment;
    });

    return (
      <div className="bg-white rounded-xl shadow-md p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
        <div className="flex items-center gap-4">
          <div className="relative w-24 h-24">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              {total === 0 ? (
                <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="20" />
              ) : (
                segments.map((seg, i) => {
                  const radius = 40;
                  const circumference = 2 * Math.PI * radius;
                  const dashLength = (seg.percentage / 100) * circumference;
                  const dashOffset = (seg.startAngle / 360) * circumference;
                  return (
                    <circle
                      key={i}
                      cx="50"
                      cy="50"
                      r={radius}
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="20"
                      strokeDasharray={`${dashLength} ${circumference}`}
                      strokeDashoffset={-dashOffset}
                    />
                  );
                })
              )}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-gray-700">{total}</span>
            </div>
          </div>
          <div className="flex-1 space-y-1">
            {segments.map((seg, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: seg.color }}></div>
                <span className="text-gray-600">{seg.key}</span>
                <span className="font-semibold ml-auto">{seg.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8 flex items-center justify-center" data-testid="admin-login-page">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-blue-700" />
            </div>
            <h1 className="text-2xl font-bold text-blue-900">Admin Login</h1>
            <p className="text-gray-600 mt-2">Enter password to access dashboard</p>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500"
                data-testid="admin-password-input"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 disabled:opacity-50" data-testid="admin-login-button">
              {loading ? "Logging in..." : "LOGIN TO DASHBOARD"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-20 pb-8 flex items-center justify-center">
        <p className="text-gray-600">Loading dashboard data...</p>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "analytics", label: "Analytics" },
    { id: "users", label: `Users (${dashboardData.summary.total_users})` },
    { id: "associates", label: `Associates (${dashboardData.summary.total_associates})` },
    { id: "businesses", label: `Businesses (${dashboardData.summary.total_businesses})` },
    { id: "posts", label: `Posts (${dashboardData.summary.total_posts})` },
  ];

  const postStatusColors = ["#22c55e", "#3b82f6", "#a855f7", "#f97316", "#ef4444"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-20 pb-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-6 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-blue-200">Milanam Business Intelligence</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchDashboardData} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
              Refresh
            </button>
            <button onClick={() => setIsLoggedIn(false)} className="px-4 py-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition-colors" data-testid="admin-logout-button">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 text-white">
            <p className="text-blue-100 text-xs">Total Accounts</p>
            <p className="text-3xl font-bold">{dashboardData.summary.total_accounts || 0}</p>
            <p className="text-blue-200 text-xs mt-1">+{dashboardData.today?.new_registrations || 0} today</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-4 text-white">
            <p className="text-green-100 text-xs">Users</p>
            <p className="text-3xl font-bold">{dashboardData.summary.total_users}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-4 text-white">
            <p className="text-purple-100 text-xs">Associates</p>
            <p className="text-3xl font-bold">{dashboardData.summary.total_associates}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-4 text-white">
            <p className="text-orange-100 text-xs">Businesses</p>
            <p className="text-3xl font-bold">{dashboardData.summary.total_businesses}</p>
          </div>
          <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-4 text-white">
            <p className="text-pink-100 text-xs">Service Requests</p>
            <p className="text-3xl font-bold">{dashboardData.summary.total_posts}</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl shadow-lg p-4 text-white">
            <p className="text-cyan-100 text-xs">Enquiries</p>
            <p className="text-3xl font-bold">{dashboardData.summary.total_contacts}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-2 flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id ? "bg-white text-blue-700" : "text-white hover:bg-white/20"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Charts Row */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Daily Registrations Chart */}
              {dashboardData.daily_registrations && (
                <BarChart data={dashboardData.daily_registrations} title="Daily Registrations (Last 7 Days)" color="blue" />
              )}
              
              {/* Post Status Distribution */}
              {dashboardData.post_statuses && (
                <DonutChart 
                  data={dashboardData.post_statuses} 
                  title="Service Request Status" 
                  colors={postStatusColors}
                />
              )}
              
              {/* Conversion Rates */}
              {dashboardData.conversion_rates && (
                <div className="bg-white rounded-xl shadow-md p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Conversion Funnel</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Enquiry → Quote</span>
                        <span className="font-semibold">{dashboardData.conversion_rates.enquiry_to_quote}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${dashboardData.conversion_rates.enquiry_to_quote}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Quote → Scheduled</span>
                        <span className="font-semibold">{dashboardData.conversion_rates.quote_to_scheduled}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${dashboardData.conversion_rates.quote_to_scheduled}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Scheduled → Completed</span>
                        <span className="font-semibold">{dashboardData.conversion_rates.scheduled_to_completed}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${dashboardData.conversion_rates.scheduled_to_completed}%` }}></div>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700 font-medium">Overall Conversion</span>
                        <span className="font-bold text-green-600">{dashboardData.conversion_rates.overall_conversion}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* City Stats */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">City-wise Distribution</h2>
              {Object.keys(dashboardData.city_stats || {}).length === 0 ? (
                <p className="text-gray-500">No city data available yet</p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(dashboardData.city_stats).map(([city, stats]) => (
                    <div key={city} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800">{city}</h4>
                      <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                        <div><span className="text-blue-600 font-bold">{stats.users}</span><br/>Users</div>
                        <div><span className="text-green-600 font-bold">{stats.associates}</span><br/>Associates</div>
                        <div><span className="text-purple-600 font-bold">{stats.businesses}</span><br/>Business</div>
                        <div><span className="text-orange-600 font-bold">{stats.posts}</span><br/>Posts</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Session Analytics */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <p className="text-4xl font-bold text-blue-600">{dashboardData.analytics?.page_views || 0}</p>
                <p className="text-gray-600 text-sm">Total Page Views</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <p className="text-4xl font-bold text-green-600">{dashboardData.analytics?.unique_sessions || 0}</p>
                <p className="text-gray-600 text-sm">Unique Sessions</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <p className="text-4xl font-bold text-purple-600">{dashboardData.analytics?.avg_session_duration_formatted || "N/A"}</p>
                <p className="text-gray-600 text-sm">Avg Session Duration</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <p className="text-4xl font-bold text-orange-600">{dashboardData.today?.new_registrations || 0}</p>
                <p className="text-gray-600 text-sm">Today's Registrations</p>
              </div>
            </div>

            {/* User Behavior Insights */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">User Behavior Insights</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Registration Trend</h4>
                  <div className="space-y-2">
                    {(dashboardData.daily_registrations || []).map((day, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-16 text-xs text-gray-600">{day.date?.slice(5)}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all" style={{ width: `${Math.min((day.total / 5) * 100, 100)}%` }}></div>
                        </div>
                        <span className="text-xs font-semibold w-8">{day.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">User Type Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-blue-700">Users (Hire Associate)</span>
                      <span className="font-bold text-blue-700">{dashboardData.summary.total_users}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-green-700">Associates (Service Providers)</span>
                      <span className="font-bold text-green-700">{dashboardData.summary.total_associates}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <span className="text-purple-700">Business Partners</span>
                      <span className="font-bold text-purple-700">{dashboardData.summary.total_businesses}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Restraints / Drop-off Analysis */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Conversion Analysis & Restraints</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Funnel Drop-off Points</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <span>Enquiries without quotes</span>
                      <span className="font-bold text-yellow-700">{100 - (dashboardData.conversion_rates?.enquiry_to_quote || 0)}% drop</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <span>Quotes not scheduled</span>
                      <span className="font-bold text-orange-700">{100 - (dashboardData.conversion_rates?.quote_to_scheduled || 0)}% drop</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <span>Scheduled not completed</span>
                      <span className="font-bold text-red-700">{100 - (dashboardData.conversion_rates?.scheduled_to_completed || 0)}% drop</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Recommendations</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    {(dashboardData.conversion_rates?.enquiry_to_quote || 0) < 50 && (
                      <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <p className="font-medium text-blue-800">Improve Quote Response</p>
                        <p className="text-blue-600">Encourage associates to respond faster to enquiries</p>
                      </div>
                    )}
                    {(dashboardData.conversion_rates?.quote_to_scheduled || 0) < 50 && (
                      <div className="p-3 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                        <p className="font-medium text-purple-800">Better Price Matching</p>
                        <p className="text-purple-600">Quotes may not be competitive - review pricing</p>
                      </div>
                    )}
                    {dashboardData.summary.total_posts === 0 && (
                      <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                        <p className="font-medium text-green-800">Drive Engagement</p>
                        <p className="text-green-600">No service requests yet - promote to users</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Registered Users</h2>
            {dashboardData.data.users.length === 0 ? (
              <p className="text-gray-500">No users registered yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Phone/Email</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">City</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Joined</th>
                      <th className="text-center py-3 px-4 text-gray-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.data.users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-blue-50">
                        <td className="py-3 px-4 font-medium">{user.name}</td>
                        <td className="py-3 px-4 text-sm">{user.email || user.phone || "-"}</td>
                        <td className="py-3 px-4">{user.city || "-"}</td>
                        <td className="py-3 px-4 text-sm text-gray-500">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</td>
                        <td className="py-3 px-4 text-center">
                          <button onClick={() => handleDelete("user", user.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Associates Tab */}
        {activeTab === "associates" && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Registered Associates</h2>
            {dashboardData.data.associates.length === 0 ? (
              <p className="text-gray-500">No associates registered yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Phone/Email</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">City</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Services</th>
                      <th className="text-center py-3 px-4 text-gray-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.data.associates.map((assoc) => (
                      <tr key={assoc.id} className="border-b border-gray-100 hover:bg-green-50">
                        <td className="py-3 px-4 font-medium">{assoc.name}</td>
                        <td className="py-3 px-4 text-sm">{assoc.email || assoc.phone || "-"}</td>
                        <td className="py-3 px-4">{assoc.city || "-"}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {assoc.services?.slice(0, 2).map((s, i) => (
                              <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{s}</span>
                            ))}
                            {assoc.services?.length > 2 && <span className="text-xs text-gray-500">+{assoc.services.length - 2}</span>}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button onClick={() => handleDelete("associate", assoc.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Businesses Tab */}
        {activeTab === "businesses" && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Business Partners</h2>
            {dashboardData.data.businesses.length === 0 ? (
              <p className="text-gray-500">No businesses registered yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Business Name</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Owner</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Phone/Email</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">City</th>
                      <th className="text-center py-3 px-4 text-gray-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.data.businesses.map((biz) => (
                      <tr key={biz.id} className="border-b border-gray-100 hover:bg-purple-50">
                        <td className="py-3 px-4 font-medium">{biz.business_name}</td>
                        <td className="py-3 px-4">{biz.owner_name}</td>
                        <td className="py-3 px-4 text-sm">{biz.email || biz.phone || "-"}</td>
                        <td className="py-3 px-4">{biz.city || "-"}</td>
                        <td className="py-3 px-4 text-center">
                          <button onClick={() => handleDelete("business", biz.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === "posts" && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Service Requests</h2>
            {dashboardData.data.posts.length === 0 ? (
              <p className="text-gray-500">No service requests yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Title</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Service</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">City</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Status</th>
                      <th className="text-center py-3 px-4 text-gray-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.data.posts.map((post) => (
                      <tr key={post.id} className="border-b border-gray-100 hover:bg-orange-50">
                        <td className="py-3 px-4 font-medium">{post.title}</td>
                        <td className="py-3 px-4">{post.service_type || "-"}</td>
                        <td className="py-3 px-4">{post.city || "-"}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            post.status === "completed" ? "bg-green-100 text-green-700" :
                            post.status === "scheduled" ? "bg-purple-100 text-purple-700" :
                            post.status === "quoted" ? "bg-blue-100 text-blue-700" :
                            post.status === "cancelled" ? "bg-red-100 text-red-700" :
                            "bg-gray-100 text-gray-700"
                          }`}>{post.status}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button onClick={() => handleDelete("post", post.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <TopNavBar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/register-associate" element={<RegisterAssociatePage />} />
            <Route path="/hire-associate" element={<HireAssociatePage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/b2b-services" element={<B2BServicesPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          {/* Bottom navbar removed for cleaner website design */}
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
